/* eslint-disable prefer-const */
import { Workstream, WorkPackage, Task, PlanBuilderContext, TaskGroupEdit } from "./Plan"
import WorkstreamObj from "./WorkstreamObj";
import WorkstreamBuilder from "./WorkstreamBuilder";
import WorkPackageBuilder from "./WorkPackageBuilder";
import TaskBuilder from "./TaskBuilder";
import ProjectBuilder from "./ProjectBuilder";
import React, { useState } from "react";
import { Input } from "../ui/input";
import { AppContext, WorkPackageOptions } from "@/App";
import { isWorkingDay } from "@/utils/GlobalConstants";
import { formatDate } from "date-fns";
import { DeliverableIcon, CheckIcon, StatusReportIcon, CharterIcon, MilestoneIcon, DependenciesIcon, UserIcon, ArrowUpIcon, ArrowDownIcon, EditIcon, MultiSelectIcon, MultiAddIcon, ChartIcon, ManageTeamIcon, UnassignedIcon, GanttChartIcon, SelectedIcon /*, DownloadIcon*/ } from "../ui/Icons/TooltipIcon";
import TrafficLightComponent from "../TrafficLightComponent";
//import { DndContext, DragEndEvent, DragOverEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
    DragDropProvider,
    PointerSensor,
    KeyboardSensor,
    DragOverlay,
} from '@dnd-kit/react';

import GroupEditTasksBuilder from "./GroupEditTasksBuilder";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import FrmAddMultipleTasks from "./frmAddMultipleTasks";
import TeamBuilder from "../TeamBuilder/team_builder_core/TeamBuilder";
import ProjectObj from "./ProjectObj";
import GanttControls from "./plan_gantt/GanttControls";
import { GanttContext, GanttScale } from "./plan_gantt/Gantt";
import Confirmation from "@/hooks/confirmation";
import FrmUpdateTaskName from "./frmUpdateTaskName";
import { isSortable } from "@dnd-kit/react/sortable";
import TaskObj from "./TaskObj";
import { arrayMove } from "@dnd-kit/sortable";
import { PageLockContext } from "../PageLockProvider";
import HelpLink from "../ui/help-link";
// import { PDFViewer } from "@react-pdf/renderer";
// import ExportGanttPDF from "./plan_gantt/ExportGanttPDF";

interface PlanBuilderProps {
    startDate: Date;
    projectLifecycleStage: string;
    workstreams: Workstream[]
    setWorkstreams: React.Dispatch<React.SetStateAction<Workstream[]>>;
    passedTaskId?: number;
    filterCombo?: number;
    passedWorkstreamId?: number;
    showEditFormInitial?: boolean;
    resetPassedValues?: () => void;
}

const PlanBuilder = ({ startDate, projectLifecycleStage, workstreams, setWorkstreams, passedTaskId, filterCombo, passedWorkstreamId, showEditFormInitial, resetPassedValues }: PlanBuilderProps) => {

    const app = React.useContext(AppContext);
    const lock = React.useContext(PageLockContext);

    const projectTypeId = app.selectedProject?.projectTypeId ?? 1; // default to 1 if no project is selected
    const [allTasks, setAllTasks] = useState<Task[]>([]);
    const [updateTaskList, setUpdateTaskList] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);
    const [dialogTitle, setDialogTitle] = useState<string>("");

    const [selectedWorkstreamID, setSelectedWorkstreamID] = useState<number | null>(null);
    const [selectedWorkpackageID, setSelectedWorkpackageID] = useState<number | null>(null);
    const [selectedTaskID, setSelectedTaskID] = useState<number | null>(null);
    const [showEditForm, setShowEditForm] = useState(showEditFormInitial ?? false);
    const [multiSelect, setMultiSelect] = useState(false);
    const [showChart, setShowChart] = useState(false);
    const [disableAddMultiTasks, setDisableAddMultiTasks] = useState(true);

    const [showGantt, setShowGantt] = useState(false);
    const [displayLength, setDisplayLength] = useState<number>(30); // default to 30 days
    const [displayStartDate, setDisplayStartDate] = useState<Date>(startDate);
    const [projectStartDate, setProjectStartDate] = useState<Date>(startDate);
    const [projectEndDate, setProjectEndDate] = useState<Date>(new Date());
    const [ganttScale, setGanttScale] = useState<GanttScale>("day");

    const [selectedTaskIDs, setSelectedTaskIDs] = useState<number[]>([]);
    const [openWorkstreams, setOpenWorkstreams] = useState<number[] | null>(null);
    const [openWorkpackages, setOpenWorkpackages] = useState<number[] | null>(null);
    const [navLevel, setNavLevel] = useState(0); // nothing selected = 0, workstream selected = 1, workpackage selected = 2, task selected = 3

    //#region Filters
    const [enableFilterBySelected, setEnableFilterBySelected] = useState(false);

    const [filterByDeliverable, setFilterByDeliverable] = useState(false);
    const [filterByMilestone, setFilterByMilestone] = useState(false);
    const [filterByCharter, setFilterByCharter] = useState(false);
    const [filterByStatusReport, setFilterByStatusReport] = useState(false);
    const [filterByCompleted, setFilterByCompleted] = useState(false);
    const [filterByDependencies, setFilterByDependencies] = useState(false);
    const [filterByOwner, setFilterByOwner] = useState(false);
    const [filterByUnassigned, setFilterByUnassigned] = useState(false);
    const [filterByStatusRed, setFilterByStatusRed] = useState(false);
    const [filterByStatusAmber, setFilterByStatusAmber] = useState(false);
    const [filterCombination, setFilterCombination] = useState(0);
    const [filterBySelected, setFilterBySelected] = useState(false);

    const [searchText, setSearchText] = useState("");

    const [movingTask, setMovingTask] = useState<Task | null>(null);
    const [movingTaskOriginWP, setMovingTaskOriginWP] = useState<number | null>(null);

    React.useEffect(() => {
        let filterCombo = 0;
        if (filterByDeliverable) {
            filterCombo += 1;
        }
        if (filterByMilestone) {
            filterCombo += 2;
        }
        if (filterByCharter) {
            filterCombo += 4;
        }
        if (filterByStatusReport) {
            filterCombo += 8;
        }
        if (filterByCompleted) {
            filterCombo += 16;
        }
        if (filterByDependencies) {
            filterCombo += 32;
        }
        if (filterByOwner) {
            filterCombo += 64;
        }
        if (filterByStatusRed) {
            filterCombo += 128;
        }
        if (filterByStatusAmber) {
            filterCombo += 256;
        }
        if (filterByUnassigned) {
            filterCombo += 512;
        }
        if (filterBySelected) {
            filterCombo += 1024;
        }
        setFilterCombination(filterCombo);

    }, [filterByDeliverable, filterByMilestone, filterByCharter, filterByStatusReport, filterByCompleted, filterByDependencies, filterByOwner, filterByUnassigned, filterByStatusRed, filterByStatusAmber, filterBySelected]);

    React.useEffect(() => {
        const currentOpenWorkstreams = openWorkstreams;
        drawFilteredTasks();

        if (currentOpenWorkstreams != null) {
            setOpenWorkstreams(currentOpenWorkstreams);
        }
    }, [searchText, filterCombination]);

    React.useEffect(() => {
        // if filters are in place, draw the filtered tasks
        if (filterCombination !== 0 || searchText !== "") {
            drawFilteredTasks();
        }
        //getProjectStartDate();
        getProjectLength();
    }, [workstreams]);

    React.useEffect(() => {
        if (passedTaskId !== undefined) {
            selectTask(passedTaskId);
        }
    }, [passedTaskId]);

    React.useEffect(() => {
        if (selectedTaskID === null) {
            setFilterByDependencies(false);
        }

        if (filterBySelected) {
            drawFilteredTasks();
        }
    }, [selectedTaskID]);

    React.useEffect(() => {
        if (multiSelect || (selectedTaskID === null && selectedWorkpackageID === null)) {
            setDisableAddMultiTasks(true);
            return;
        }

        if (selectedTaskID !== null) {
            try {
                const task = getTaskById(selectedTaskID);
                task.level === 2 ? setDisableAddMultiTasks(true) : setDisableAddMultiTasks(false);
            } catch {
                // Task no longer exists (e.g., was just deleted), disable multi-task add
                setDisableAddMultiTasks(true);
            }
            return;
        }

        setDisableAddMultiTasks(false);
    }, [selectedTaskID, selectedWorkpackageID, multiSelect]);

    React.useEffect(() => {
        if (selectedTaskIDs === null || selectedTaskIDs.length === 0) {
            setFilterBySelected(false);
            setEnableFilterBySelected(false);
        } else {
            setEnableFilterBySelected(true);
        }

        if (filterBySelected && selectedTaskIDs.length > 0) {
            drawFilteredTasks();
        }

    }, [selectedTaskIDs]);

    React.useEffect(() => {
        if (filterCombo !== undefined) {
            setFilterCombination(filterCombo);
            setFilterVariablesFromCombo(filterCombo);
        }
    }, [filterCombo]);

    React.useEffect(() => {
        if (passedWorkstreamId !== undefined) {
            setOpenWorkstreams(null);
            selectWorkstream(passedWorkstreamId);
            setOpenWorkstreams([passedWorkstreamId]);
        }
    }, [passedWorkstreamId]);

    // this should only be called once when the page has finished loading
    React.useEffect(() => {
        resetPassedValues?.();

        if (app.teamMembers.length == 0 && app.projectManagers.length == 0) {
            openTeamBuilderDialog();
        }
    }, []);

    function containsFilterCombination(task: Task) {
        let filterCombo = 0;
        if (task.isDeliverable) {
            filterCombo += 1;
        }
        if (task.isMilestone) {
            filterCombo += 2;
        }
        if (task.displayOnCharter) {
            filterCombo += 4;
        }
        if (task.displayOnStatusReport) {
            filterCombo += 8;
        }
        if (task.completed) {
            filterCombo += 16;
        }
        if (selectedTaskID !== null && getDependencyTree(selectedTaskID).includes(task.id)) {
            filterCombo += 32;
        }
        if (task.ownerId === app.applicationUser?.id) {
            filterCombo += 64;
        }
        if (task.status === 1) {
            filterCombo += 128;
        }
        if (task.status === 2) {
            filterCombo += 256;
        }
        if (task.ownerId === "" || task.ownerId === null || task.ownerId === undefined) {
            filterCombo += 512;
        }
        if (selectedTaskIDs.includes(task.id) || selectedTaskID === task.id) {
            filterCombo += 1024;
        }

        if (filterCombination === 0) {
            return true; // filter not set so return true for everything
        }

        return (filterCombo & filterCombination) === filterCombination; // bitwise AND to check if all bits are set
    }

    function containsSearchText(task: Task) {
        if (searchText === "") {
            return true;
        }

        return task.name.toLowerCase().includes(searchText.toLowerCase());
        // || task.description.toLowerCase().includes(searchText.toLowerCase());
    }

    function drawFilteredTasks() {
        //openTree(true);
        const filteredTasks = getAllTasks().filter(task => containsSearchText(task) && containsFilterCombination(task));
        getAllTasks().forEach(task => {
            if (!filteredTasks.includes(task)) {
                document.getElementById("t" + task.id)?.classList.add("hidden");
            } else {
                document.getElementById("t" + task.id)?.classList.remove("hidden");
            }
        });

        workstreams.forEach((ws) => {
            let containsFilteredWP = false;
            ws.workPackages.forEach((wp) => {
                // if all tasks in a workpackage are hidden, hide the workpackage
                if (filteredTasks.length > 0 && !filteredTasks.some(task => task.workPackageId === wp.id)) {
                    document.getElementById("wp" + wp.id)?.classList.add("hidden");
                } else {
                    document.getElementById("wp" + wp.id)?.classList.remove("hidden");
                    setOpenWorkpackages((prevOpenWorkpackages) => {
                        if (prevOpenWorkpackages === null) {
                            return [wp.id];
                        }
                        return [...prevOpenWorkpackages, wp.id];
                    });
                    containsFilteredWP = true;
                }
            });

            // if all workpackages in a workstream are hidden, hide the workstream
            if (!containsFilteredWP) {
                document.getElementById("ws" + ws.id)?.classList.add("hidden");
            } else {
                document.getElementById("ws" + ws.id)?.classList.remove("hidden");
                //openWorkstream(ws.id);
            }
        });
    }

    function setFilterVariablesFromCombo(filterCombo: number) {
        setFilterByDeliverable((filterCombo & 1) === 1);
        setFilterByMilestone((filterCombo & 2) === 2);
        setFilterByCharter((filterCombo & 4) === 4);
        setFilterByStatusReport((filterCombo & 8) === 8);
        setFilterByCompleted((filterCombo & 16) === 16);
        setFilterByDependencies((filterCombo & 32) === 32);
        setFilterByOwner((filterCombo & 64) === 64);
        setFilterByStatusRed((filterCombo & 128) === 128);
        setFilterByStatusAmber((filterCombo & 256) === 256);
        setFilterByUnassigned((filterCombo & 512) === 512);
    }

    // function closeAllWorkstreamsButOne(workstreamID: number) {
    //     setOpenWorkstreams([workstreamID]);
    //     workstreams.forEach((ws) => {
    //         if (ws.id !== workstreamID) {
    //             closeWorkstream(ws.id);
    //         }
    //     });
    // }

    const [openStateAll, setOpenStateAll] = useState(true);

    function openTree(open: boolean = true) {
        if (open) {
            setOpenWorkstreams(workstreams.map((ws) => ws.id));

            setOpenWorkpackages(workstreams.flatMap((ws) => ws.workPackages.map((wp) => wp.id)));
        }
        else {
            setOpenWorkstreams(null);
            setOpenWorkpackages(null);
        }
        setOpenStateAll(open);
    }
    //#endregion

    //#region Project Actions
    function toggleGantt() {
        if (!showGantt) {
            setShowChart(false);
            setShowGantt(true);
            openTree(true);
        } else {
            setShowGantt(false);
        }
    }

    function toggleChart() {
        if (!showChart) {
            setShowChart(true);
            setShowGantt(false);
            openTree(false);
        } else {
            setShowChart(false);
        }
    }

    function getProjectLength() {
        // get earliest start date and latest finish date of all workpackages
        // Flatten all workPackages from all workstreams
        const allWorkPackages = workstreams.flatMap(ws => ws.workPackages);

        if (allWorkPackages.length === 0) {
            setDisplayLength(7);
            setGanttScale("day");
            setProjectEndDate(new Date());
            return;
        }

        let startDate = new Date(allWorkPackages[0].startDate).setHours(0, 0, 0, 0);
        let finishDate = new Date(allWorkPackages[0].finishDate).setHours(0, 0, 0, 0);

        if (allWorkPackages.length > 1) {
            for (let i = 1; i < allWorkPackages.length; i++) {
                if (new Date(allWorkPackages[i].startDate).setHours(0, 0, 0, 0) < startDate) {
                    startDate = new Date(allWorkPackages[i].startDate).setHours(0, 0, 0, 0);
                }
                if (new Date(allWorkPackages[i].finishDate).setHours(0, 0, 0, 0) > finishDate) {
                    finishDate = new Date(allWorkPackages[i].finishDate).setHours(0, 0, 0, 0);
                }
            }
        }
        setScaleByDateRange(new Date(startDate), new Date(finishDate));
        setProjectEndDate(new Date(finishDate));
    }

    function scaleToSelected() {
        if (multiSelect && selectedTaskIDs.length > 0) {
            const selectedTasks = getAllTasks().filter(t => selectedTaskIDs.includes(t.id));
            if (selectedTasks.length > 0) {
                const earliestStart = new Date(Math.min(...selectedTasks.map(t => new Date(t.startDate).setHours(0, 0, 0, 0))));
                const latestFinish = new Date(Math.max(...selectedTasks.map(t => new Date(t.finishDate).setHours(0, 0, 0, 0))));
                setScaleByDateRange(new Date(earliestStart), new Date(latestFinish));
            }
        } else if (selectedTaskID !== null) {
            const task = getAllTasks().find(t => t.id === selectedTaskID);
            if (task) {
                const earliestStart = new Date(task.startDate).setHours(0, 0, 0, 0);
                const latestFinish = new Date(task.finishDate).setHours(0, 0, 0, 0);
                setScaleByDateRange(new Date(earliestStart), new Date(latestFinish));
            }
        } else if (selectedWorkpackageID !== null) {
            const workpackage = getWorkPackageById(selectedWorkpackageID);
            if (workpackage) {
                const earliestStart = new Date(workpackage.startDate).setHours(0, 0, 0, 0);
                const latestFinish = new Date(workpackage.finishDate).setHours(0, 0, 0, 0);
                setScaleByDateRange(new Date(earliestStart), new Date(latestFinish));
            }
        }
        else if (selectedWorkstreamID !== null) {
            const workstream = workstreams.find(ws => ws.id === selectedWorkstreamID);
            if (workstream) {
                // Calculate earliest start and latest finish from all workpackages in the workstream
                const workPackages = workstream.workPackages;
                if (workPackages.length === 0) {
                    return;
                }
                const earliestStart = Math.min(...workPackages.map(wp => new Date(wp.startDate).setHours(0, 0, 0, 0)));
                const latestFinish = Math.max(...workPackages.map(wp => new Date(wp.finishDate).setHours(0, 0, 0, 0)));
                setScaleByDateRange(new Date(earliestStart), new Date(latestFinish));
            }
        }
        else {
            // if nothing is selected, set the gantt to the whole project
            getProjectLength();
        }
    }

    function setScaleByDateRange(earliestStart: Date, latestFinish: Date) {
        const lengthInDays = Math.round((new Date(latestFinish).getTime() - new Date(earliestStart).getTime()) / (1000 * 60 * 60 * 24)) + 1; // add one to include the start date
        setDisplayStartDate(earliestStart);

        if (lengthInDays > 30 && lengthInDays < 120) {
            setGanttScale("week");
            setDisplayLength(getProjectLengthInFullWeeks(lengthInDays, new Date(earliestStart))); // default to 4 weeks
        } else if (lengthInDays >= 120 && lengthInDays < 365) {
            setGanttScale("month");
            setDisplayLength(Math.ceil(lengthInDays / 30)); // default to 4 months
        } else if (lengthInDays >= 365) {
            setGanttScale("year");
            setDisplayLength(Math.ceil(lengthInDays / 365)); // default to 1 year
        } else {
            setGanttScale("day");
            setDisplayLength(lengthInDays); // return the number of days between start and finish date
        }
    }

    function getProjectLengthInFullWeeks(lengthInDays: number, startDate: Date): number {
        if (startDate.getDay() !== 1) { // if the start date is not a Sunday, adjust it to the next Sunday
            return Math.ceil(lengthInDays / 7) + 1; // round up to the nearest week and add one to include the start date
        }
        return Math.ceil(lengthInDays / 7); // round up to the nearest week
    }

    //#endregion

    //#region Project Actions

    function selectProject() {
        setSelectedTaskID(null);
        setSelectedWorkpackageID(null);
        setSelectedWorkstreamID(null);
        setNavLevel(0);
    }

    function updateProjectStartDate(startDate: Date) {

        document.body.style.cursor = 'wait';
        fetch(`Plan/UpdateProjectStartDate/${app.selectedProject?.id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(formatDate(startDate, "yyyy-MM-dd")),
        }).then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        }).then((data: Task[]) => {
            if (data.length > 0) {
                updateTasksInStateRecursively(data);
            }
            setProjectStartDate(startDate);

        }).catch((error) => {
            console.error('Error:', error);
            app.showActionNotification("Error", "Error updating project start date", "destructive");
            return;
        }).finally(() => {
            document.body.style.cursor = 'default';
        });
    }

    //#endregion

    //#region Workstream Actions

    function selectWorkstream(workstreamID: number) {
        if (projectTypeId === 1) {
            if (selectedWorkstreamID === null) {
                setSelectedWorkstreamID(workstreams[0].id);
            }
            return;
        }
        if (selectedWorkstreamID === workstreamID) {
            if (selectedWorkpackageID !== null) {
                setSelectedWorkpackageID(null);
                setSelectedTaskID(null);
                setNavLevel(1);
                return;
            }
            setSelectedWorkstreamID(null);
            setSelectedWorkpackageID(null);
            setSelectedTaskID(null);
            setNavLevel(0);
            return;
        }
        setSelectedWorkstreamID(workstreamID);
        setSelectedWorkpackageID(null);
        setSelectedTaskID(null);
        setNavLevel(1);
    }

    function navigateWorkStreams(direction: -1 | 0 | 1) {
        if (selectedWorkstreamID === null && direction !== 0) {
            return;
        }
        let workstreamIndex = workstreams.findIndex((workstream) => workstream.id === selectedWorkstreamID);
        if (workstreamIndex === workstreams.length - 1 && direction === 1) {
            workstreamIndex = -1; // negative one because we add one to the index before selecting the workstream
        }
        else if (workstreamIndex === 0 && direction === -1) {
            workstreamIndex = workstreams.length;
        }
        selectWorkstream(workstreams[workstreamIndex + direction].id);
    }

    function addWorkstream() {
        if (app.selectedProject !== undefined) {
            document.body.style.cursor = 'wait';
            fetch("Plan/AddWorkstream/" + app.selectedProject?.id, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error("Failed to add Workstream");
                    }
                    return response.json();
                })
                .then((data) => {
                    const newWorkstream = { id: data.id, name: data.name, projectId: data.projectId, active: data.active, workPackages: data.workPackages };
                    setWorkstreams([...workstreams, newWorkstream]);
                    setSelectedWorkstreamID(newWorkstream.id);
                    setNavLevel(1);
                    setOpenWorkstreams((prevOpenWorkstreams) => {
                        if (prevOpenWorkstreams === null) {
                            return [newWorkstream.id];
                        }
                        else {
                            return [...prevOpenWorkstreams, newWorkstream.id];
                        }
                    });
                    setSelectedWorkpackageID(null);
                    setOpenWorkpackages((prevOpenWorkpackages) => {
                        if (prevOpenWorkpackages === null) {
                            return newWorkstream.workPackages.map((wp: WorkPackage) => wp.id);
                        }
                        else {
                            return [...prevOpenWorkpackages, ...newWorkstream.workPackages.map((wp: WorkPackage) => wp.id)];
                        }
                    });
                    setSelectedTaskID(null);

                    // update workstream in workpackage options
                    const newWorkstreamOption: WorkPackageOptions = {
                        workstreamId: newWorkstream.id,
                        workstreamName: newWorkstream.name,
                        workPackageNames: newWorkstream.workPackages.map((wp: WorkPackage) => ({ workPackageId: wp.id, workPackageName: wp.name })),
                    }
                    app.setWorkPackageOptions([...app.workPackageOptions!, newWorkstreamOption]);
                    ShowSavedMessage(newWorkstream.name);
                    document.body.style.cursor = 'default';
                })
                .catch(() => {
                    app.showActionNotification("Error", "Error adding new workstream", "destructive");
                    document.body.style.cursor = 'default';
                });
        }
    }

    async function updateWorkstream(workstreamID: number, workstream: Workstream) {

        document.body.style.cursor = 'wait';
        fetch("Plan/UpdateWorkstream", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ "id": workstream.id, "name": workstream.name, "active": workstream.active }),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to update workstream");
                }
            })
            .then(() => {
                setWorkstreams(workstreams.map((ws) => {
                    if (ws.id === workstreamID) {
                        return workstream;
                    }
                    return ws;
                }));

                // update workstream in workpackage options
                app.setWorkPackageOptions(app.workPackageOptions!.map((ws) => {
                    if (ws.workstreamId === workstreamID) {
                        return { ...ws, workstreamName: workstream.name };
                    }
                    return ws;
                }));
                ShowSavedMessage(workstream.name);
                document.body.style.cursor = 'default';
            })
            .catch(() => {
                app.showActionNotification("Error", "Error updating workstream", "destructive");
                document.body.style.cursor = 'default';
            });
    }

    function deleteWorkstream(workstreamID: number) {
        // select next available workstream
        if (workstreams.length === 1) {
            setSelectedWorkstreamID(null);
            resetWorkstream(); // instead of deleting the lastworkstream just to add another one, reset the workstream to default values
            return;
        } else {
            navigateWorkStreams(1);
        }
        document.body.style.cursor = 'wait';
        fetch("Plan/Workstream/" + workstreamID, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to delete workstream");
                }
            })
            .then(() => {
                setWorkstreams(workstreams.filter((ws) => ws.id !== workstreamID));

                // remove workstream from workpackage options
                app.setWorkPackageOptions(app.workPackageOptions!.filter((ws) => ws.workstreamId !== workstreamID));
                app.showActionNotification("Success", "Workstream deleted", "success");
                document.body.style.cursor = 'default';
            })
            .catch(() => {
                app.showActionNotification("Error", "Error deleting workstream", "destructive");
                document.body.style.cursor = 'default';
            });
    }

    function resetWorkstream() {
        if (workstreams.length === 1) {
            workstreams[0].name = "WS1";
            workstreams[0].workPackages = [];
            updateWorkstream(workstreams[0].id, workstreams[0]).then(() => {
                addWorkPackage(); // add a workpackage to the workstream
            });
        }
    }

    function toggleWorkstream(workstreamID: number) {
        if (openWorkstreams === null || !openWorkstreams.includes(workstreamID)) {
            openWorkstream(workstreamID);
        }
        else {
            closeWorkstream(workstreamID);
        }
    }

    function openWorkstream(workstreamID: number) {
        if (openWorkstreams === null || !openWorkstreams.includes(workstreamID)) {
            if (openWorkstreams === null) {
                setOpenWorkstreams([workstreamID]);
            }
            else {
                setOpenWorkstreams([...openWorkstreams, workstreamID]);
            }
        }
    }

    function closeWorkstream(workstreamID: number) {
        if (openWorkstreams === null || !openWorkstreams.includes(workstreamID)) {
            return;
        }
        setOpenWorkstreams(openWorkstreams?.filter((id) => id !== workstreamID));
    }

    // function getWorkstreamByID(workstreamID: number) {
    //     return workstreams.find((workstream) => workstream.id === workstreamID);
    // }

    //#endregion

    //#region WorkPackage Actions

    function selectWorkPackage(workpackageID: number) {
        // ensure parent workstream is selected
        const workstream = workstreams.find((workstream) => workstream.workPackages.find((workpackage) => workpackage.id === workpackageID)) as Workstream;
        setSelectedWorkstreamID(workstream.id);

        if (selectedWorkpackageID === workpackageID) {
            if (selectedTaskID !== null) {
                setSelectedTaskID(null);
                setNavLevel(2);
                return;
            }
            if (projectTypeId === 1) {
                selectProject();
                return;
            }
            else {
                setSelectedWorkpackageID(null);
                setSelectedTaskID(null);
                setNavLevel(1);
                return;
            }
        }
        setSelectedWorkpackageID(workpackageID);
        setSelectedTaskID(null);
        setNavLevel(2);
    }

    function navigateWorkPackages(direction: -1 | 0 | 1) {
        if (selectedWorkpackageID === null && direction !== 0) {
            return;
        }
        const workstream = (workstreams.find((workstream) => workstream.id === selectedWorkstreamID) ?? workstreams[0]) as Workstream;

        if (direction === 0) {
            // make sure workstream is open
            if (openWorkstreams === null || !openWorkstreams.includes(selectedWorkstreamID as number)) {
                openWorkstream(selectedWorkstreamID as number);
            }
            if (selectedTaskID !== null) {
                selectTask(selectedTaskID as number); // this toggles the task off
                return;
            }
            else {
                selectWorkPackage(workstream.workPackages[0].id);
                return;
            }
        }

        let workpackageIndex = workstream.workPackages.findIndex((workpackage) => workpackage.id === selectedWorkpackageID);
        if (workpackageIndex === workstream.workPackages.length - 1 && direction === 1) {
            workpackageIndex = -1; // negative one because we add one to the index before selecting the workpackage
        }
        else if (workpackageIndex === 0 && direction === -1) {
            workpackageIndex = workstream.workPackages.length;
        }
        selectWorkPackage(workstream.workPackages[workpackageIndex + direction].id);
    }

    function addWorkPackage() {
        if (selectedWorkstreamID === null && projectTypeId === 2) {
            return; // do not allow adding workpackage without a workstream in a project with multiple workstreams
        }

        if (projectTypeId === 2) {
            openWorkstream(selectedWorkstreamID!);
        }

        const workstreamId = projectTypeId === 2 ? selectedWorkstreamID : workstreams[0].id;
        document.body.style.cursor = 'default';
        fetch("Plan/WorkPackage/" + workstreamId, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to add workpackage");
                }
                return response.json();
            })
            .then((data) => {
                const newWorkPackage = { id: data.id, name: data.name, active: data.active, startDate: data.startDate, finishDate: data.finishDate, ownerId: data.ownerId, index: data.index, tasks: data.tasks };
                addWorkPackageToState(newWorkPackage);
                setSelectedWorkpackageID(newWorkPackage.id);
                setOpenWorkpackages((prevOpenWorkpackages) => {
                    if (prevOpenWorkpackages === null) {
                        return [newWorkPackage.id];
                    }
                    else {
                        return [...prevOpenWorkpackages, newWorkPackage.id];
                    }
                });
                setSelectedTaskID(null);
                setNavLevel(2);

                // update workPackage Options
                const existingWorkstreamOption = app.workPackageOptions?.find((ws) => ws.workstreamId === workstreamId);
                if (existingWorkstreamOption) {
                    app.setWorkPackageOptions(app.workPackageOptions!.map((ws) => {
                        if (ws.workstreamId === workstreamId) {
                            return { ...ws, workPackageNames: [...ws.workPackageNames, { workPackageId: newWorkPackage.id, workPackageName: newWorkPackage.name }] }
                        }
                        return ws;
                    }));
                }

                ShowSavedMessage(newWorkPackage.name);
                document.body.style.cursor = 'default';
            })
            .catch(() => {
                app.showActionNotification("Error", "Error adding new workpackage", "destructive");
                document.body.style.cursor = 'default';
            });
    }

    function updateWorkPackage(workpackage: WorkPackage) {
        document.body.style.cursor = 'wait';
        fetch("Plan/WorkPackage", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ "id": workpackage.id, "name": workpackage.name, "startDate": formatDate(workpackage.startDate, "yyyy-MM-dd"), "finishDate": workpackage.finishDate, "ownerID": workpackage.ownerId }),
        })
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 400) {
                        return response.json().then((error) => {
                            throw new Error(error.error || "Bad Request");
                        });
                    }
                    throw new Error("Failed to update workpackage");
                }
                return response.json();
            })
            .then((data) => {
                updateWorkPackageInState(data);

                // update workPackage Options
                app.setWorkPackageOptions(app.workPackageOptions!.map((ws) => {
                    if (ws.workstreamId === selectedWorkstreamID) {
                        return {
                            ...ws, workPackageNames: ws.workPackageNames.map((wp) => {
                                if (wp.workPackageId === workpackage.id) {
                                    return { ...wp, workPackageName: workpackage.name }
                                }
                                return wp;
                            })
                        }
                    }
                    return ws;
                }));
                ShowSavedMessage(workpackage.name);
                document.body.style.cursor = 'default';
            })
            .catch((error) => {

                app.showActionNotification("Error", error.message || "Error updating workpackage", "destructive");
                document.body.style.cursor = 'default';
            });
    }

    function deleteWorkPackage(workpackageID: number) {
        document.body.style.cursor = 'wait';
        setSelectedWorkpackageID(null); // clear the selected workpackage ready for deletion
        fetch("Plan/WorkPackage/" + workpackageID, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => {
                if (response.ok) {
                    // select next available workpackage
                    navigateWorkPackages(1);
                    let workstreamID = -1;
                    setWorkstreams(workstreams.map((ws) => {
                        if (ws.workPackages.length === 1 && ws.workPackages[0].id === workpackageID) {
                            workstreamID = ws.id;
                        }
                        return {
                            ...ws,
                            workPackages: ws.workPackages.filter((wp) => wp.id !== workpackageID)
                        }
                    }
                    ));
                    // if the workstream only had one workpackage, delete the workstream
                    const updatedWs = workstreams.find(ws => ws.id === workstreamID);
                    if (updatedWs && updatedWs.workPackages.filter(wp => wp.id !== workpackageID).length === 0) {
                        deleteWorkstream(workstreamID);
                    }

                    // remove workpackage from workPackage Options
                    app.setWorkPackageOptions(app.workPackageOptions!.map((ws) => {
                        if (ws.workstreamId === selectedWorkstreamID) {
                            return { ...ws, workPackageNames: ws.workPackageNames.filter((wp) => wp.workPackageId !== workpackageID) }
                        }
                        return ws;
                    }));
                    setFilterByDependencies(false);
                    app.showActionNotification("Success", "Workpackage deleted", "success");
                    setUpdateTaskList(true);
                } else {
                    if (response.status === 400) {
                        response.json().then((error) => {
                            app.showActionNotification("Action Disallowed", error.error || "Bad Request", "default");
                        });
                    } else {
                        app.showActionNotification("Error", response.statusText, "destructive");
                    }
                }
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            })
            .catch((e) => {
                app.showActionNotification("Error", e.message, "destructive");
                document.body.style.cursor = 'default';
            });
    }

    function confirmDeleteWorkPackage(workpackageID: number) {
        const workpackage = getWorkPackageById(workpackageID);
        if (!workpackage) {
            app.showActionNotification("Error", "Workpackage not found", "destructive");
            return;
        }
        setDialogContent(<Confirmation
            content={<p>Are you sure you want to delete "{workpackage.name}"?</p>}
            cancelText="Cancel"
            confirmText="Delete"
            onCancel={() => { setDialogOpen(false); }}
            onConfirm={() => { deleteWorkPackage(workpackageID); setDialogOpen(false); }}
        />);
        setDialogTitle("Delete Work Package?");
        setDialogOpen(true);
    }

    function addWorkPackageToState(workpackage: WorkPackage) {
        const workstreamId = workstreams[0].id; // only one worksteam at the moment
        setWorkstreams(workstreams.map((ws) => {
            if (ws.id === workstreamId) {
                return {
                    ...ws,
                    workPackages: [...ws.workPackages, workpackage]
                };
            }
            return ws;
        }));
        setUpdateTaskList(true);
    }

    function updateWorkPackageInState(workpackage: WorkPackage) {

        setWorkstreams(workstreams.map((ws) => {
            return {
                ...ws,
                workPackages: ws.workPackages.map((wp) => {
                    if (wp.id === workpackage.id) {
                        return {
                            ...wp,
                            name: workpackage.name,
                            ownerId: workpackage.ownerId,
                            startDate: new Date(workpackage.startDate),
                            finishDate: new Date(workpackage.finishDate)
                        };
                    }
                    return wp;
                })
            }
        }));

        if (workpackage.tasks && workpackage.tasks.length > 0) {
            updateTasksInStateRecursively(workpackage.tasks);
        }

        setUpdateTaskList(true);
    }

    function toggleWorkPackage(workpackageID: number) {
        if (openWorkpackages === null || !openWorkpackages.includes(workpackageID)) {
            openWorkpackage(workpackageID);
        }
        else {
            closeWorkPackage(workpackageID);
        }
    }

    function openWorkpackage(workpackageID: number) {
        if (openWorkpackages === null || !openWorkpackages.includes(workpackageID)) {
            if (openWorkpackages === null) {
                setOpenWorkpackages([workpackageID]);
            }
            else {
                setOpenWorkpackages([...openWorkpackages, workpackageID]);
            }
        }
    }

    function closeWorkPackage(workpackageID: number) {
        if (openWorkpackages === null || !openWorkpackages.includes(workpackageID)) {
            return;
        }
        setOpenWorkpackages(openWorkpackages.filter((id) => id !== workpackageID));
    }

    function getWorkPackageById(workpackageID: number): WorkPackage | undefined {
        return workstreams[0].workPackages.find((workpackage) => workpackage.id === workpackageID);
    }

    //#endregion

    //#region Task actions

    const selectTask = (taskID: number, workPackageId?: number, event?: React.MouseEvent<HTMLDivElement>) => {
        if (selectedTaskID === taskID) {
            setSelectedTaskID(null);
            setNavLevel(2);
            return;
        }
        //const workstream = workstreams.find((workstream) => workstream.workPackages.find((workpackage) => workpackage.tasks.find((task) => task.id === taskID))) as Workstream;
        if (workPackageId === undefined) {
            const task = getTaskById(taskID);
            if (!task) {
                console.error("Task not found with ID:", taskID);
                return;
            }
            workPackageId = task.workPackageId;
        }

        setSelectedWorkstreamID(workstreams[0].id);
        openWorkstream(workstreams[0].id);
        setSelectedWorkpackageID(workPackageId);
        openWorkpackage(workPackageId);
        setSelectedTaskID(taskID);
        setNavLevel(3);
        if (event && event.ctrlKey) {
            selectMultiTask(taskID, event);
            setMultiSelect(true);
        }
    };

    function selectMultiTask(taskID: number, event?: React.MouseEvent<HTMLDivElement>) {
        if (selectedTaskIDs.length === 0 && selectedTaskID == null) {
            setSelectedTaskIDs([taskID]);
            selectTask(taskID);
            return;
        }

        setSelectedTaskIDs((prevSelectedTaskIDs) => {
            // Always work with a copy to avoid mutating React state directly
            let prev = [...prevSelectedTaskIDs];

            if (prev.length === 0 && selectedTaskID === null) {
                selectTask(taskID);
                return [taskID];
            }

            if (event && event.ctrlKey) {
                if (prev.length === 0 && selectedTaskID !== null) {
                    prev = [...prev, selectedTaskID];
                }
                // select all tasks between the last selected task and the current task
                const lastSelectedTaskID = prev[prev.length - 1];

                // Get visible task IDs in the order they are displayed on the screen
                const taskIDs = getVisibleTasks();
                const lastSelectedIndex = taskIDs.indexOf(lastSelectedTaskID);
                const currentTaskIndex = taskIDs.indexOf(taskID);
                if (lastSelectedIndex === -1 || currentTaskIndex === -1) {
                    return prev; // if the last selected task or current task is not found, do nothing
                }
                const startIndex = Math.min(lastSelectedIndex, currentTaskIndex);
                const endIndex = Math.max(lastSelectedIndex, currentTaskIndex);
                const tasksToSelect = taskIDs.slice(startIndex, endIndex + 1); // include the current task
                if (tasksToSelect.length === 0) {
                    return prev; // if no tasks are selected, do nothing
                }
                // if the last selected task is already in the list, remove it and add the new range
                if (prev.includes(lastSelectedTaskID)) {
                    const filtered = prev.filter((id) => !tasksToSelect.includes(id)).concat(tasksToSelect);
                    return Array.from(new Set(filtered));
                }
                // otherwise, add the tasks to the list
                return Array.from(new Set([...prev, ...tasksToSelect])); // use Set to avoid duplicates
            }

            if (event && event.altKey) {
                setSelectedTaskID(taskID);
                return [...prev];
            }

            if (prev.includes(taskID)) {
                return prev.filter((id) => id !== taskID);
            } else {
                return [...prev, taskID];
            }
        });
    }

    function getVisibleTasks() {
        const openWsIds = projectTypeId === 2 ? openWorkstreams ?? [] : [workstreams[0].id];
        const openWpIds = openWorkpackages ?? [];

        // Helper to recursively collect visible task IDs (including subtasks)
        function collectVisibleTaskIds(tasks: Task[]): number[] {
            let ids: number[] = [];
            for (const task of tasks) {
                // Only include if not hidden
                if (!document.getElementById("t" + task.id)?.classList.contains("hidden")) {
                    ids.push(task.id);
                }
                if (task.subTasks && task.subTasks.length > 0) {
                    ids = ids.concat(collectVisibleTaskIds(task.subTasks));
                }
            }
            return ids;
        }

        // Get all visible tasks (and subtasks) in open workstreams and workpackages
        let visibleTaskIdsInOpen: number[] = [];
        workstreams
            .filter(ws => openWsIds.includes(ws.id))
            .forEach(ws => {
                ws.workPackages
                    .filter(wp => openWpIds.includes(wp.id))
                    .forEach(wp => {
                        visibleTaskIdsInOpen = visibleTaskIdsInOpen.concat(collectVisibleTaskIds(wp.tasks));
                    });
            });

        return visibleTaskIdsInOpen;
    }

    function toggleMultiSelect(event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        if (event && event.ctrlKey && !multiSelect) {
            const visibleTaskIds = getVisibleTasks();

            setSelectedTaskIDs(visibleTaskIds);
            if (!selectedTaskID) {
                // if no task is selected, select the first task in the list
                if (visibleTaskIds.length > 0) {
                    selectTask(visibleTaskIds[0]);
                }
            }

            setMultiSelect(!multiSelect);
            return;
        }

        if (selectedTaskID) {
            setSelectedTaskIDs([selectedTaskID]);
        } else {
            setSelectedTaskIDs([]);
        }

        if (multiSelect) { // multiSelect is about to be turned off
            setSelectedTaskIDs([]);
        }
        setMultiSelect(prev => !prev);
    }

    function navigateTasks(direction: -1 | 0 | 1) {
        if (selectedTaskID === null && direction !== 0) {
            return;
        }
        // Get visible task IDs in the order they are displayed on the screen
        const taskIDs = getVisibleTasks();

        if (direction === 0) {
            // make sure workpackage is open
            if (openWorkpackages === null || !openWorkpackages.includes(selectedWorkpackageID as number)) {
                openWorkpackage(selectedWorkpackageID as number);
            }
            selectTask(getWorkPackageById(selectedWorkpackageID!)!.tasks[0].id);
            return;
        }
        let taskIndex = taskIDs.indexOf(selectedTaskID!);
        if (taskIndex === taskIDs.length - 1 && direction === 1) {
            taskIndex = -1; // negative one because we add one to the index before selecting the task
        }
        else if (taskIndex === 0 && direction === -1) {
            taskIndex = taskIDs.length;
        }
        selectTask(taskIDs[taskIndex + direction]);
    }

    function getAllTasks(): Task[] {
        if (!updateTaskList) {
            return allTasks;
        }
        const taskList: Task[] = [];
        workstreams[0].workPackages.forEach((workPackage) => {
            taskList.push(...workPackage.tasks);
        });

        const addSubTasksRecursively = (tasks: Task[]) => {
            tasks.forEach((task) => {
                if (task.subTasks && task.subTasks.length > 0) {
                    taskList.push(...task.subTasks);
                    addSubTasksRecursively(task.subTasks);
                }
            });
        }

        addSubTasksRecursively(taskList);
        setAllTasks(taskList); // store all tasks in state for future use
        setUpdateTaskList(false);
        return taskList;
    }

    function getTaskById(taskID: number): Task {
        const task = getAllTasks().find(t => t.id === taskID);
        if (task) {
            return task;
        } else {
            // rebuild allTasks in case it was not up to date
            setUpdateTaskList(true);
            const rebuiltTask = getAllTasks().find(t => t.id === taskID);
            if (rebuiltTask) {
                return rebuiltTask;
            } else {
                throw new Error(`Task with ID ${taskID} not found`);
            }
        }
    }

    function addTask(WorkPackageID: number = selectedWorkpackageID as number, task?: Task) {
        const url = task ? "Plan/Task/" + WorkPackageID + "/" + task.id : "Plan/Task/" + WorkPackageID;

        document.body.style.cursor = 'wait';
        fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to add task");
                }
                return response.json();
            })
            .then((data) => {
                const newTask = { id: data.id, name: data.name, index: data.index, workPackageId: data.workPackageId, duration: data.duration, startDate: data.startDate, finishDate: data.finishDate, ownerId: data.ownerId, completed: data.completed, completedDate: data.completedDate, isDeliverable: data.isDeliverable, isMilestone: data.isMilestone, displayOnCharter: data.displayOnCharter, displayOnStatusReport: data.displayOnStatusReport, dependencies: data.dependencies, hasDependents: data.hasDependents, status: data.status, parentTaskId: data.parentTaskId, subTasks: data.subTasks };
                if (task) {
                    addTasksToParent([newTask], task.id);
                }
                else {
                    addTaskstoWorkPackage([newTask], WorkPackageID);
                }
                ShowSavedMessage(newTask.name);
            })
            .catch((e) => {
                app.showActionNotification("Error", e.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function updateTask(taskID: number, task: Task) {
        const updatedTasksForMessage: string[] = [];

        // prevent circular dependencies
        if (task.dependencies.includes(taskID)) {
            return;
        }
        document.body.style.cursor = "wait";
        fetch("Plan/Task", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(
                {
                    "id": task.id,
                    "name": task.name,
                    "startDate": formatDate(task.startDate, "yyyy-MM-dd"),
                    "finishDate": formatDate(task.finishDate, "yyyy-MM-dd"),
                    "ownerId": task.ownerId,
                    "completed": task.completed,
                    "completedDate": task.completedDate,
                    "isDeliverable": task.isDeliverable,
                    "isMilestone": task.isMilestone,
                    "displayOnCharter": task.displayOnCharter,
                    "displayOnStatusReport": task.displayOnStatusReport,
                    "dependencies": task.dependencies,
                    "parentTaskId": task.parentTaskId
                }),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed update task");
                }
                return response.json();
            })
            .then((data: Task[]) => {
                updateTasksInStateRecursively(data);
                updatedTasksForMessage.push(...data.map(t => t.name));
                ShowSavedMessage(updatedTasksForMessage.length > 0 ? `Updated tasks: ${updatedTasksForMessage.join(", ")}` : task.name);
            })
            .catch((e) => {
                console.log(e);
                app.showActionNotification("Error", "Error updating task", "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function groupEditTasks(taskGroupEdit: TaskGroupEdit) {
        // set cursor to loading
        document.body.style.cursor = "wait";

        fetch("Plan/Tasks/TaskGroup", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(taskGroupEdit),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed update tasks");
                }
                return response.json();
            })
            .then((updatedTasks: Task[]) => {
                // if workpackage has changed
                if (taskGroupEdit.enableWorkPackage) {
                    taskGroupEdit.taskIDs.forEach(taskId => {
                        const task = getTaskById(taskId);

                        // first remove tasks from the workpackages including subtasks
                        if (task.parentTaskId) {
                            removeTaskFromParent(task);
                        } else {
                            removeTaskFromWorkPackage(task, task.workPackageId);
                        }
                        // add task to the new workpackage
                        addTaskstoWorkPackage([task], taskGroupEdit.workPackage!);
                    });
                    setSelectedWorkstreamID(taskGroupEdit.workstream);
                    setSelectedWorkpackageID(taskGroupEdit.workPackage);
                }

                if (taskGroupEdit.enableSetSubtasks) {
                    const newParentTask = getTaskById(taskGroupEdit.highlightedTaskId!);
                    newParentTask.subTasks = newParentTask.subTasks || [];
                    // remove the selected tasks from their current position, either parent task or workpackage
                    taskGroupEdit.taskIDs.forEach(taskId => {
                        if (taskId === newParentTask.id) return; // skip if the task is the new parent task

                        const task = getTaskById(taskId);
                        if (task.parentTaskId) {
                            removeTaskFromParent(task);
                        } else {
                            removeTaskFromWorkPackage(task, task.workPackageId);
                        }
                        // add the selected tasks to the new parent task
                        addTasksToParent([task], newParentTask.id);
                    });
                }

                updateTasksInStateRecursively(updatedTasks);

                app.showActionNotification("Success", `${updatedTasks.length} Tasks updated`, "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", error.message, "destructive");
            });
        // reset cursor
        document.body.style.cursor = "default";
    }

    function updateTasksInStateRecursively(updatedTasks: Task[]) {
        // Create a map for quick lookup by id
        const updatedTaskMap = new Map<number, Task>();
        updatedTasks.forEach(task => updatedTaskMap.set(task.id, task));

        function updateTasks(tasks: Task[]): Task[] {
            return tasks.map((t) => {
                const updated = updatedTaskMap.get(t.id);
                if (updated) {
                    updated.duration = getWorkingDays(new Date(updated.startDate), new Date(updated.finishDate));
                }
                // Preserve subtasks, don't update them here
                let newTask = updated
                    ? { ...t, ...updated, subTasks: t.subTasks }
                    : t;
                if (newTask.subTasks && newTask.subTasks.length > 0) {
                    newTask = { ...newTask, subTasks: updateTasks(newTask.subTasks) };
                }
                return newTask;
            });
        }

        setWorkstreams((prevWorkstreams) =>
            prevWorkstreams.map((ws) => ({
                ...ws,
                workPackages: ws.workPackages.map((wp) => {
                    const updatedTasks = updateTasks(wp.tasks);
                    // Update startDate and finishDate based on updated tasks
                    let startDate = wp.startDate;
                    let finishDate = wp.finishDate;
                    if (updatedTasks.length > 0) {
                        startDate = new Date(Math.min(...updatedTasks.map(t => new Date(t.startDate).getTime())));
                        finishDate = new Date(Math.max(...updatedTasks.map(t => new Date(t.finishDate).getTime())));
                    }
                    return {
                        ...wp,
                        tasks: updatedTasks,
                        startDate,
                        finishDate,
                    };
                }),
            }))
        );
        setUpdateTaskList(true);
    }

    function resetHasDependentsFlags(dependencies: number[]) {
        setWorkstreams((prevWorkstreams) =>
            prevWorkstreams.map((ws) => ({
                ...ws,
                workPackages: ws.workPackages.map((wp) => {
                    function unsetDependents(tasks: Task[]): Task[] {
                        return tasks.map((t) => {
                            if (!dependencies.includes(t.id)) {
                                // if the task is not in the dependencies list, leave it unchanged
                                return t;
                            }
                            if (t.subTasks && t.subTasks.length > 0) {
                                return { ...t, subTasks: unsetDependents(t.subTasks) };
                            }
                            const dependentTaskIds: number[] = [];
                            getDependentTaskIds(t.id, dependentTaskIds);
                            return { ...t, hasDependents: dependentTaskIds.length > 0 };
                        });
                    }
                    return {
                        ...wp,
                        tasks: unsetDependents(wp.tasks),
                    };
                }),
            }))
        );
        setUpdateTaskList(true);
    }

    function removeTaskFromParent(task: Task) {
        // Capture the parentId at the time of call to avoid relying on the task object being mutated elsewhere
        const parentId = task.parentTaskId;
        if (parentId === null || parentId === undefined) {
            return;
        }

        // Recursively remove the specified childId from the subTasks of the tree
        function removeFromSubTasks(tasks: Task[]): Task[] {
            return tasks.map((t) => {
                // If this is the parent, remove the subtask from its subTasks
                if (t.id === parentId && t.subTasks) {
                    return {
                        ...t,
                        subTasks: t.subTasks.filter((subTask) => subTask.id !== task.id),
                    };
                }
                // If this task has subtasks, recurse into them
                if (t.subTasks && t.subTasks.length > 0) {
                    return {
                        ...t,
                        subTasks: removeFromSubTasks(t.subTasks),
                    };
                }
                return t;
            });
        }

        setWorkstreams((prevWorkstreams) =>
            prevWorkstreams.map((ws) => ({
                ...ws,
                workPackages: ws.workPackages.map((wp) => {
                    if (wp.id === task.workPackageId) {
                        const updatedTasks = removeFromSubTasks(wp.tasks);
                        const startDate = updatedTasks.length > 0
                            ? new Date(Math.min(...updatedTasks.map(t => new Date(t.startDate).getTime())))
                            : wp.startDate;
                        const finishDate = updatedTasks.length > 0
                            ? new Date(Math.max(...updatedTasks.map(t => new Date(t.finishDate).getTime())))
                            : wp.finishDate;
                        return {
                            ...wp,
                            tasks: updatedTasks,
                            startDate,
                            finishDate,
                        };
                    }
                    return wp;
                }),
            }))
        );
        setUpdateTaskList(true);
    }

    function addTasksToParent(tasksToAdd: Task[], parentTaskID: number) {
        const earliestStart = new Date(Math.min(...tasksToAdd.map(t => new Date(t.startDate).getTime())));
        const latestFinish = new Date(Math.max(...tasksToAdd.map(t => new Date(t.finishDate).getTime())));
        // Ensure each task's parentTaskId is set to the new parentTaskID
        tasksToAdd.forEach(task => {
            task.parentTaskId = parentTaskID;
        });

        function addToSubTasks(tasks: Task[]): Task[] {
            return tasks.map((t) => {
                // If this is the parent, add the subtasks to its subTasks
                if (t.id === parentTaskID) {
                    return {
                        ...t,
                        subTasks: t.subTasks ? [...t.subTasks, ...tasksToAdd] : [...tasksToAdd],
                    };
                }
                // If this task has subtasks, recurse into them
                if (t.subTasks && t.subTasks.length > 0) {
                    return {
                        ...t,
                        subTasks: addToSubTasks(t.subTasks),
                    };
                }
                return t;
            });
        }

        // Assume all tasks to add have the same workPackageId as the parent
        const workPackageId = tasksToAdd.length > 0 ? tasksToAdd[0].workPackageId : undefined;
        if (!workPackageId) return;

        setWorkstreams((prevWorkstreams) =>
            prevWorkstreams.map((ws) => ({
                ...ws,
                workPackages: ws.workPackages.map((wp) => {
                    if (wp.id === workPackageId) {
                        return {
                            ...wp,
                            tasks: addToSubTasks(wp.tasks),
                            startDate: new Date(Math.min(...wp.tasks.map(t => new Date(t.startDate).getTime()), earliestStart.getTime())),
                            finishDate: new Date(Math.max(...wp.tasks.map(t => new Date(t.finishDate).getTime()), latestFinish.getTime())),
                        };
                    }
                    return wp;
                }),
            }))
        );
        setUpdateTaskList(true);
        setSelectedTaskID(tasksToAdd[0].id);
        if (tasksToAdd.length > 1) {
            setSelectedTaskIDs(tasksToAdd.map(t => t.id));
            setMultiSelect(true);
        }
        setNavLevel(3);
    }

    function indentTasksUnderParent() {
        if (selectedTaskIDs.length === 0 && selectedTaskID === null) {
            app.showActionNotification("Action Disallowed", "No tasks selected for indentation", "default");
            return;
        }

        const tasksToIndent: Task[] = [];
        if (multiSelect) {
            tasksToIndent.push(...selectedTaskIDs.map(taskId => getTaskById(taskId)).sort((a, b) => a.index - b.index));
        } else {
            const singleTask = getTaskById(selectedTaskID!);
            if (singleTask) {
                tasksToIndent.push(singleTask);
            }
        }

        if (tasksToIndent.length === 0) {
            app.showActionNotification("Action Disallowed", "No tasks selected for indentation", "default");
            return;
        }

        function getNearestTaskForParent(): Task | null {
            if (tasksToIndent.length === 0) return null;
            const firstTask = tasksToIndent.reduce((min, t) => (t.index < min.index ? t : min), tasksToIndent[0]);
            if (!firstTask) {
                return null;
            }
            if (firstTask.level === 2) {
                throw new Error("Cannot indent, maximum subtask level reached");
            }

            // get unique parent IDs of selected tasks
            const parentIds = tasksToIndent.map(t => t.parentTaskId ?? 0).filter((id, index, self) => self.indexOf(id) === index) as number[];
            if (parentIds.length > 1) {
                throw new Error("Cannot indent, tasks must have the same parent for indentation");
            }

            // If tasks are top-level (no parent), search for nearest preceding top-level task in the same workpackage
            if (parentIds[0] === 0) {
                const wp = workstreams[0].workPackages.find(w => w.id === firstTask.workPackageId);
                if (!wp) {
                    return null;
                }
                const topLevelTasks = wp.tasks.filter(t => t.parentTaskId == null);
                const candidates = topLevelTasks.filter(t => t.index < firstTask.index);
                if (candidates.length === 0) {
                    return null;
                }
                const nearest = candidates.reduce((prev, cur) => (cur.index > prev.index ? cur : prev));
                return nearest;
            }

            // If tasks already have a parent, find the nearest preceding sibling in that parent's subTasks
            const parentTask = getTaskById(parentIds[0]);
            if (!parentTask) {
                return null;
            }
            const siblings = parentTask.subTasks ?? [];
            const siblingCandidates = siblings.filter(t => t.index < firstTask.index);
            if (siblingCandidates.length === 0) {
                return null;
            }
            const nearestSibling = siblingCandidates.reduce((prev, cur) => (cur.index > prev.index ? cur : prev));
            return nearestSibling;
        }

        try {
            const parentTask = getNearestTaskForParent();
            if (!parentTask) {
                app.showActionNotification("Action Disallowed", "No valid parent task found for indentation", "default");
                return;
            }

            const taskGroupEditPayload: TaskGroupEdit = {
                highlightedTaskId: parentTask.id,
                taskIDs: tasksToIndent.map(t => t.id),
                enableOwnerId: false,
                enableSetSubtasks: true,
                enableWorkPackage: false,
                enableDisplayOnCharter: false,
                enableDisplayOnStatusReport: false,
                enableIsDeliverable: false,
                enableIsMilestone: false,
                enableDuration: false,
                enableSetDependencies: false,
                ownerId: "",
                workstream: 0,
                workPackage: 0,
                isDeliverable: false,
                isMilestone: false,
                displayOnCharter: false,
                displayOnStatusReport: false,
                duration: 0,
                setDependencies: "selected",
                setSubtasks: true
            };

            groupEditTasks(taskGroupEditPayload);
        } catch (error: any) {
            app.showActionNotification("Error", error.message, "destructive");
            return;
        }
    }

    function outdentTasksFromParent() {
        if (selectedTaskIDs.length === 0 && selectedTaskID === null) {
            app.showActionNotification("Action Disallowed", "No tasks selected for outdentation", "default");
            return;
        }

        const tasksToOutdent = multiSelect ? selectedTaskIDs.map(taskId => getTaskById(taskId)).sort((a, b) => a.index - b.index) : [getTaskById(selectedTaskID!)];
        if (tasksToOutdent.length === 0) {
            app.showActionNotification("Action Disallowed", "No tasks selected for outdentation", "default");
            return;
        }

        const parentIds = tasksToOutdent.map(t => t.parentTaskId ?? 0).filter((id, index, self) => self.indexOf(id) === index) as number[];
        if (parentIds.length > 1) {
            app.showActionNotification("Action Disallowed", `tasks must have the same parent for outdentation`, "default");
            return;
        }
        const parentTask = parentIds[0] === 0 ? undefined : getTaskById(parentIds[0]);
        if (!parentTask) {
            app.showActionNotification("Action Disallowed", "Tasks already at root level", "default");
            return;
        }

        const taskGroupEditPayload: TaskGroupEdit = {
            highlightedTaskId: parentTask.parentTaskId ?? 0,
            taskIDs: tasksToOutdent.map(t => t.id),
            enableOwnerId: false,
            enableSetSubtasks: parentTask.parentTaskId !== null,
            enableWorkPackage: parentTask.parentTaskId === null,
            enableDisplayOnCharter: false,
            enableDisplayOnStatusReport: false,
            enableIsDeliverable: false,
            enableIsMilestone: false,
            enableDuration: false,
            enableSetDependencies: false,
            ownerId: "",
            workstream: workstreams[0].id,
            workPackage: tasksToOutdent[0].workPackageId,
            isDeliverable: false,
            isMilestone: false,
            displayOnCharter: false,
            displayOnStatusReport: false,
            duration: 0,
            setDependencies: "selected",
            setSubtasks: false
        };

        groupEditTasks(taskGroupEditPayload);
    }

    function addTaskstoWorkPackage(tasks: Task[], workPackageID: number) {
        // Ensure each task's workPackageId is set to the new workPackageID
        tasks.forEach(task => {
            task.workPackageId = workPackageID;
        });
        setWorkstreams((prevWorkstreams) =>
            prevWorkstreams.map((ws) => ({
                ...ws,
                workPackages: ws.workPackages.map((wp) => {
                    if (wp.id === workPackageID) {
                        const updatedTasks = [...wp.tasks, ...tasks];
                        updatedTasks.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
                        return {
                            ...wp,
                            tasks: updatedTasks,
                            startDate: new Date(Math.min(...updatedTasks.map(t => new Date(t.startDate).getTime()))),
                            finishDate: new Date(Math.max(...updatedTasks.map(t => new Date(t.finishDate).getTime()))),
                        };
                    }
                    return wp;
                }),
            }))
        );
        setUpdateTaskList(true);
        setSelectedWorkpackageID(workPackageID);
        openWorkpackage(workPackageID);
        setSelectedTaskID(tasks[0].id);
        if (tasks.length > 1) {
            setSelectedTaskIDs(tasks.map(t => t.id));
            setMultiSelect(true);
        }
        setNavLevel(3);
    }

    function removeTaskFromWorkPackage(task: Task, workPackageID: number) {
        setWorkstreams((prevWorkstreams) => {
                const updatedWorkstreams = prevWorkstreams.map((ws) => ({
                ...ws,
                workPackages: ws.workPackages.map((wp) => {
                    if (wp.id === workPackageID) {
                        const updatedTasks = wp.tasks.filter(t => t.id !== task.id);
                        if (wp.tasks.length === 0) {
                            return wp; // prevent calculating min/max on empty array
                        }
                        return {
                            ...wp,
                            tasks: updatedTasks,
                            startDate: updatedTasks.length > 0
                                ? new Date(Math.min(...updatedTasks.map(t => new Date(t.startDate).getTime())))
                                : wp.startDate,
                            finishDate: updatedTasks.length > 0
                                ? new Date(Math.max(...updatedTasks.map(t => new Date(t.finishDate).getTime())))
                                : wp.finishDate,
                        };
                    }
                    return wp;
                }).filter((wp) => wp.tasks.length > 0),
            }))
        rebuildWorkPackageOptions(updatedWorkstreams);
        return updatedWorkstreams;
    });
        setUpdateTaskList(true);
    }

    function removeTaskFromWorkPackageKeepEmpty(task: Task, workPackageID: number) {
        setWorkstreams((prevWorkstreams) => 
            prevWorkstreams.map((ws) => ({
                ...ws,
                workPackages: ws.workPackages.map((wp) => {
                    if (wp.id === workPackageID) {
                        const updatedTasks = wp.tasks.filter(t => t.id !== task.id);
                        return {
                            ...wp,
                            tasks: updatedTasks,
                            startDate: updatedTasks.length > 0
                                ? new Date(Math.min(...updatedTasks.map(t => new Date(t.startDate).getTime())))
                                : wp.startDate,
                            finishDate: updatedTasks.length > 0
                                ? new Date(Math.max(...updatedTasks.map(t => new Date(t.finishDate).getTime())))
                                : wp.finishDate,
                        };
                    }
                    return wp;
                })
            }))
        );
        setUpdateTaskList(true);
    }

    function deleteTask(taskID: number) {
        if (getTaskById(taskID).hasDependents) {
            app.showActionNotification("Action Disallowed", "Cannot delete task with dependents", "default");
            return;
        }
        document.body.style.cursor = 'wait';
        fetch("Plan/Task/" + taskID, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        }).then((response) => {
            if (!response.ok) {
                throw new Error("Failed to delete task");
            }
        })
            .then(() => {
                const workPackageID = getTaskById(taskID).workPackageId;
                const task = getTaskById(taskID);
                const dependencies = task.dependencies;

                if (!task) {
                    throw new Error(`Task with ID ${taskID} not found`);
                }
                // Clear selected task ID before any UI updates
                setSelectedTaskID(null);
                
                if (task.parentTaskId) {
                    removeTaskFromParent(task);
                } else {
                    if (getWorkPackageById(workPackageID)!.tasks.length === 1) {
                        confirmDeleteLastTaskInWorkPackage(workPackageID, task);
                    }
                    else {
                        removeTaskFromWorkPackage(task, workPackageID);
                    }
                }
                navigateTasks(1);
                resetHasDependentsFlags(dependencies);
            }
            )
            .catch(() => {
                app.showActionNotification("Error", "Error deleting task", "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function groupDeleteTasks(taskIds: number[]) {
        // This function is now just a wrapper for performGroupDelete
        // The work package checking is done in confirmDeleteTasks
        performGroupDelete(taskIds, [], false);
    }
    
    function performGroupDelete(taskIds: number[], emptyWorkPackages: Array<{ workPackageId: number, totalTasks: number, tasksToDelete: number }>, keepWorkPackages: boolean) {
        document.body.style.cursor = 'wait';
        // delete tasks
        fetch("Plan/Tasks", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(taskIds),
        })
            .then((response) => {
                if (response.ok) {
                    app.showActionNotification("Success", "Tasks Deleted Successfully!", "success");
                    // clear selected task IDs
                    setSelectedTaskIDs([]);
                    setSelectedWorkpackageID(null);
                    setSelectedWorkstreamID(null);
                    setMultiSelect(false); // Disable multi-select mode after deleting tasks
                    setSelectedTaskID(null);
                    
                    // update local state
                    taskIds.forEach((taskId) => {
                        const task = getTaskById(taskId);
                        if (task.parentTaskId) {
                            removeTaskFromParent(task);
                        } else {
                            const isEmptyWorkPackage = emptyWorkPackages.some(info => info.workPackageId === task.workPackageId);
                            if (isEmptyWorkPackage) {
                                removeTaskFromWorkPackageKeepEmpty(task, task.workPackageId);
                            } else {
                                removeTaskFromWorkPackage(task, task.workPackageId);
                            }
                        }
                    });
                    
                    // Handle empty work packages
                    if (emptyWorkPackages.length > 0) {
                        if (keepWorkPackages) {
                            // Add new tasks to keep the work packages
                            emptyWorkPackages.forEach(info => {
                                addTask(info.workPackageId);
                            });
                        } else {
                            // Delete the work packages
                            emptyWorkPackages.forEach(info => {
                                deleteWorkPackage(info.workPackageId);
                            });
                        }
                    }

                    setUpdateTaskList(true);
                    setNavLevel(2); // Go back to workpackage level
                } else {
                    if (response.status === 400) {
                        response.json().then((error) => {
                            app.showActionNotification("Action Disallowed", error.error || "Bad Request", "default");
                        });
                    } else {
                        app.showActionNotification("Error", response.statusText, "destructive");
                    }
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function confirmDeleteTasks(taskIds: number[]) {
        // Check if any work packages will be left empty after deletion
        const workPackagesAffected = new Map<number, { workPackageId: number, totalTasks: number, tasksToDelete: number }>();
        
        taskIds.forEach((taskId) => {
            const task = getTaskById(taskId);
            if (!task.parentTaskId) { // Only count top-level tasks
                const workPackageId = task.workPackageId;
                if (!workPackagesAffected.has(workPackageId)) {
                    const workPackage = getWorkPackageById(workPackageId);
                    workPackagesAffected.set(workPackageId, {
                        workPackageId: workPackageId,
                        totalTasks: workPackage!.tasks.length,
                        tasksToDelete: 0
                    });
                }
                const info = workPackagesAffected.get(workPackageId)!;
                info.tasksToDelete++;
            }
        });
        
        // Find work packages that will be empty
        const emptyWorkPackages = Array.from(workPackagesAffected.values()).filter(
            info => info.totalTasks === info.tasksToDelete
        );
        
        if (emptyWorkPackages.length > 0) {
            // Show work package confirmation dialog
            const workPackageNames = emptyWorkPackages.map(info => {
                const wp = getWorkPackageById(info.workPackageId);
                return wp?.name || 'Unknown';
            }).join(', ');
            
            setDialogContent(<Confirmation
                content={(<div className="mb-4">
                    <p>Deleting these tasks will leave the following Work Package(s) empty:</p>
                    <p className="font-semibold">{workPackageNames}</p>
                    <p></p>
                    <p>Would you like to delete these Work Package(s)?</p>
                    <p></p>
                    <p>If you choose to keep them, new tasks will be created.</p>
                </div>)}
                cancelText="Keep Work Package(s)"
                confirmText="Delete Work Package(s)"
                onCancel={() => { 
                    setDialogOpen(false);
                    // Keep work packages - proceed with task deletion and add new tasks
                    performGroupDelete(taskIds, emptyWorkPackages, true);
                }}
                onConfirm={() => { 
                    setDialogOpen(false);
                    // Delete work packages - proceed with task deletion
                    performGroupDelete(taskIds, emptyWorkPackages, false);
                }}
            />);
            setDialogTitle("Keep Work Package(s)?");
            setDialogOpen(true);
        } else {
            // Normal confirmation dialog
            setDialogContent(<Confirmation
                content={<p>Are you sure you want to delete the selected tasks?</p>}
                cancelText="Cancel"
                confirmText="Delete"
                onCancel={() => { setDialogOpen(false); }}
                onConfirm={() => { 
                    setDialogOpen(false);
                    performGroupDelete(taskIds, [], false); 
                }}
            />);
            setDialogTitle("Delete Tasks?");
            setDialogOpen(true);
        }
    }

    function confirmDeleteLastTaskInWorkPackage(workPackageID: number, task: Task) {
        // Clear the selectedTaskID immediately to prevent using the deleted task ID
        setSelectedTaskID(null);
        
        setDialogContent(<Confirmation
            content={(<div className="mb-4"><p>All Work Packages must contain at least one task. </p>
                <p>Would you like to delete this Work Package?</p>
                <p></p>
                <p>If you choose to keep this Work Package a new task will be created.</p></div>)}
            cancelText="Keep Work Package"
            confirmText="Delete Work Package"
            onCancel={() => { 
                // Remove the deleted task from state but keep the work package
                removeTaskFromWorkPackageKeepEmpty(task, workPackageID); 
                // Add a new task to replace it
                addTask(workPackageID); 
                setDialogOpen(false); 
            }}
            onConfirm={() => { 
                removeTaskFromWorkPackage(task, workPackageID); 
                deleteWorkPackage(workPackageID); 
                setDialogOpen(false); 
            }}
        />);
        setDialogTitle("Keep Work Package?");
        setDialogOpen(true);
    }

    function rebuildWorkPackageOptions(workstreams: Workstream[]) {
        // Rebuilds app.workPackageOptions from the current workstreams state
        const newOptions = workstreams.map((ws) => ({
            workstreamId: ws.id,
            workstreamName: ws.name,
            workPackageNames: ws.workPackages.map((wp) => ({
                workPackageId: wp.id,
                workPackageName: wp.name
            }))
        }));
        app.setWorkPackageOptions(newOptions);
    }

    function addMultipleTasks(numberOfTasks: number) {
        if (selectedWorkpackageID === null) {
            return;
        }

        const addSubtasks = selectedTaskID !== null && !multiSelect;
        document.body.style.cursor = 'wait';
        fetch("Plan/Tasks/" + selectedWorkpackageID + "/" + numberOfTasks + (addSubtasks ? "/" + selectedTaskID : ""), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to add tasks");
                }
                return response.json();
            })
            .then((data) => {
                let returnedTasks = data.map((task: Task) => ({
                    id: task.id,
                    name: task.name,
                    index: task.index,
                    workPackageId: task.workPackageId,
                    duration: task.duration,
                    startDate: task.startDate,
                    finishDate: task.finishDate,
                    ownerId: task.ownerId,
                    completed: task.completed,
                    isDeliverable: task.isDeliverable,
                    isMilestone: task.isMilestone,
                    displayOnCharter: task.displayOnCharter,
                    displayOnStatusReport: task.displayOnStatusReport,
                    dependencies: task.dependencies,
                    parentTaskId: task.parentTaskId,
                    status: task.status
                }));

                const newTasks: Task[] = [];

                if (addSubtasks) {
                    newTasks.push(...returnedTasks.filter((task: Task) => task.parentTaskId === selectedTaskID));
                    returnedTasks = returnedTasks.filter((task: Task) => task.parentTaskId !== selectedTaskID);
                    // add the new subtasks to the parent task's subTasks
                    addTasksToParent(newTasks, selectedTaskID as number);
                    // update the parent tasks
                    updateTasksInStateRecursively(returnedTasks);
                } else {
                    newTasks.push(...returnedTasks);
                    addTaskstoWorkPackage(returnedTasks, selectedWorkpackageID);
                }
                ShowSavedMessage(`${newTasks.length} Tasks added`);

                // offer to name the tasks
                setDialogContent(<Confirmation
                    onCancel={() => { setDialogOpen(false); }}
                    onConfirm={() => { setDialogContent(<FrmUpdateTaskName tasks={newTasks} setDialogOpen={setDialogOpen} />); setDialogTitle("Rename Task"); }}
                />);
                setDialogTitle(newTasks.length + " tasks added, would you like to name them now?");
                app.showActionNotification("Success", "Tasks added successfully", "success");
                setDialogOpen(true);
            })
            .catch((e) => {
                app.showActionNotification("Error", e.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function completeMultipleTasks(taskIds: number[]) {
        document.body.style.cursor = 'wait';
        fetch("Plan/Tasks/Complete", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(taskIds),
        })
            .then((response) => {
                if (response.ok) {
                    app.showActionNotification("Success", "Tasks Completed Successfully!", "success");

                    // update local state
                    taskIds.forEach((taskId) => {
                        const task = getTaskById(taskId);
                        if (task) {
                            task.completed = true;
                            task.completedDate = new Date();
                            task.status = 4;
                        }
                    }
                    );
                    setUpdateTaskList(true);
                } else {
                    if (response.status === 400) {
                        response.json().then((error) => {
                            app.showActionNotification("Action Disallowed", error.error || "Bad Request", "default");
                        });
                    } else {
                        app.showActionNotification("Error", response.statusText, "destructive");
                    }
                }
            }
            )
            .catch((error) => {
                app.showActionNotification("Error", error.message, "destructive");
            }
            )
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function getDependentNames(taskID: number) {
        // check if task exists in any other tasks dependencies
        const taskNames: string[] = [];

        getAllTasks().forEach((task) => {
            if (task.dependencies.includes(taskID)) {
                taskNames.push(task.name);
                return;
            }
        });
        return taskNames;
    }

    function getDependentTasks(taskID: number) {
        const dependentTasks: Task[] = [];
        getAllTasks().forEach((task) => {
            if (task.dependencies.includes(taskID)) {
                dependentTasks.push(task);
            }
        });

        return dependentTasks;
    }

    function getDependentTaskIds(taskID: number, dependentTaskIds: number[]) {
        const allTasks = getAllTasks();
        const visited = new Set<number>();
        const stack: number[] = [taskID];

        while (stack.length > 0) {
            const currentId = stack.pop()!;
            allTasks.forEach((task) => {
                if (task.dependencies.includes(currentId) && !visited.has(task.id)) {
                    dependentTaskIds.push(task.id);
                    stack.push(task.id);
                    visited.add(task.id);
                }
            });
        }

        const task = getTaskById(taskID);
        if (task.parentTaskId !== null) {
            getParentLineage(task, dependentTaskIds);
        }
    }

    function getParentLineage(subTask: Task, lineage: number[]) {
        // this should only ever reach a depth of 2 or 3 so should not be a performance issue
        getAllTasks().forEach((task) => {
            if (task.id === subTask.parentTaskId) {
                lineage.push(task.id);
                if (task.parentTaskId !== null) {
                    getParentLineage(task, lineage);
                }
            }
        });
    }

    function getDependencies(taskID: number, dependencies: number[]) {
        const allTasks = getAllTasks();
        const visited = new Set<number>();
        const stack: number[] = [taskID];

        while (stack.length > 0) {
            const currentId = stack.pop()!;
            if (visited.has(currentId)) continue;
            visited.add(currentId);

            const task = allTasks.find(t => t.id === currentId);
            if (!task) continue;

            // Add subTasks
            if (task.subTasks && task.subTasks.length > 0) {
                for (const subTask of task.subTasks) {
                    if (!visited.has(subTask.id)) {
                        dependencies.push(subTask.id);
                        stack.push(subTask.id);
                    }
                }
            }

            // Add dependencies
            if (task.dependencies && task.dependencies.length > 0) {
                for (const depId of task.dependencies) {
                    if (!visited.has(depId)) {
                        dependencies.push(depId);
                        stack.push(depId);
                    }
                }
            }
        }
    }

    function getDependencyTree(taskId: number) {
        const task = getTaskById(taskId);

        let dependentTaskIds: number[] = [];
        getDependentTaskIds(task.id, dependentTaskIds);
        let dependencies: number[] = [...task.dependencies];
        getDependencies(task.id, dependencies);
        return [...dependentTaskIds, task.id, ...dependencies];
    }

    //#endregion

    //#region Date Functions

    function updateDependentDates(task: Task) {
        if (task === null) {
            return;
        }

        const dependentTasks = getDependentTasks(task.id);
        dependentTasks.forEach((dependentTask) => {
            dependentTask.startDate = (task as Task).finishDate;
            dependentTask.finishDate = addWorkingDays(dependentTask.startDate, dependentTask.duration);

            if (getDependentTasks(dependentTask.id).length > 0) {
                updateDependentDates(dependentTask);
            }
        });
    }

    function addWorkingDays(date: Date, days: number) {
        const newDate = new Date(date);
        while (days > 1) {
            newDate.setDate(newDate.getDate() + 1);
            if (isWorkingDay(newDate)) {
                days--;
            }
        }
        return newDate;
    }

    function getWorkingDays(from: Date, to: Date): number {
        let workingDays = 0;
        const currentDate = new Date(from);

        while (currentDate <= to) {
            const dayOfWeek = currentDate.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) { // 0 = Sunday, 6 = Saturday  
                workingDays++;
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return workingDays;
    }
    //#endregion

    //#region Content

    function saveWorkPackageOrder(workstreamId: number) {
        // update the index of the workpackages in the workstream
        const workstream = workstreams.find((ws) => ws.id === workstreamId);
        if (!workstream) {
            console.error("Workstream not found with ID:", workstreamId);
            return;
        }
        const workPackageOrderData: Array<{ Id: number, Index: number }> = workstream.workPackages.map((wp, index) => ({
            Id: wp.id,
            Index: index
        }));

        fetch(`Plan/WorkPackages/Order/${workstreamId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(workPackageOrderData),
        })
            .then((response) => {
                if (response.ok) {
                    app.showActionNotification("Success", "Work package order saved", "success");
                } else {
                    if (response.status === 400) {
                        response.json().then((error) => {
                            app.showActionNotification("Action Disallowed", error.error || "Bad Request", "default");
                        });
                    } else {
                        app.showActionNotification("Error", response.statusText, "destructive");
                    }
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", error.message, "destructive");
            });
    }

    function saveTaskOrder(workPackageId: number) {
        // update the index of the tasks in the workpackage
        const workPackage = getWorkPackageById(workPackageId);

        const taskOrderData: Array<{ index: number, id: number }> = workPackage!.tasks.map((task, index) => ({
            index: index,
            id: task.id
        }));

        fetch(`Plan/Tasks/Order/${workPackage?.id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(taskOrderData),
        })
            .then((response) => {
                if (response.ok) {
                    app.showActionNotification("Success", "Task order saved", "success");
                } else {
                    if (response.status === 400) {
                        response.json().then((error) => {
                            app.showActionNotification("Action Disallowed", error.error || "Bad Request", "default");
                        });
                    } else {
                        app.showActionNotification("Error", response.statusText, "destructive");
                    }
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", error.message, "destructive");
            });
    }

    function handleDragEnd(targetWorkPackageStr: string, activeTaskId: string, targetId: string) {
        if (targetWorkPackageStr) {
            const taskId = parseInt(activeTaskId.substring(1));
            const task = movingTask || getTaskById(taskId);

            if (!task) return;
            const targetWorkPackageId = parseInt(targetWorkPackageStr.substring(2));

            if (movingTaskOriginWP !== task.workPackageId) {
                fetch("Plan/Tasks/Move", {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        "taskId": taskId,
                        "WorkPackageId": targetWorkPackageId,
                    }),
                })
                    .then((response) => {
                        if (response.ok) {
                            //Update state in a single batch to avoid race conditions

                            setWorkstreams((prevWorkstreams) => {
                                const updatedWorkstreams = prevWorkstreams
                                    .map((workstream) => ({
                                        ...workstream,
                                        workPackages: workstream.workPackages
                                            .map((workpackage) => {
                                                if (workpackage.id === targetWorkPackageId || workpackage.id === movingTaskOriginWP) {
                                                    // If there are tasks, calculate the earliest start date and latest finish date
                                                    if (workpackage.tasks.length > 0) {
                                                        const startDates = workpackage.tasks.map(t => new Date(t.startDate));
                                                        const finishDates = workpackage.tasks.map(t => new Date(t.finishDate));

                                                        // Find earliest start date
                                                        workpackage.startDate = new Date(Math.min(...startDates.map(d => d.getTime())));

                                                        // Find latest finish date
                                                        workpackage.finishDate = new Date(Math.max(...finishDates.map(d => d.getTime())));
                                                    }
                                                }
                                                return workpackage;
                                            })
                                            .filter((wp) => wp.tasks.length > 0)
                                    }))
                                    .filter((ws) => ws.workPackages.length > 0);

                                task.workPackageId = targetWorkPackageId; // Update the task's work package ID

                                const currentWorkPackage = workstreams[0].workPackages.find((wp) => wp.id === targetWorkPackageId);
                                const wpTasks = currentWorkPackage!.tasks;

                                currentWorkPackage!.tasks =
                                    arrayMove(
                                        wpTasks,
                                        wpTasks.findIndex((t) => t.id === taskId),
                                        wpTasks.findIndex((t) => t.id === parseInt(targetId.substring(1)))
                                    );

                                saveTaskOrder(targetWorkPackageId);

                                rebuildWorkPackageOptions(updatedWorkstreams);
                                selectWorkPackage(targetWorkPackageId);
                                setSelectedTaskID(taskId);
                                setNavLevel(3);
                                app.showActionNotification("Success", "Task moved", "success");

                                return updatedWorkstreams;
                            });
                        } else {
                            if (response.status === 400) {
                                response.json().then((error) => {
                                    app.showActionNotification("Action Disallowed", error.error || "Bad Request", "default");
                                });
                            } else {
                                app.showActionNotification("Error", response.statusText, "destructive");
                            }
                        }
                    })
                    .catch((error) => {
                        app.showActionNotification("Error", error.message, "destructive");
                    });
            }
            else {
                saveTaskOrder(targetWorkPackageId);

            }

            setMovingTask(null); // Clear movingTask after the operation
            setMovingTaskOriginWP(null); // Reset the origin work package

            // reset the task's isMoving state
            workstreams.forEach((workstream) => {
                workstream.workPackages.forEach((workpackage) => {
                    workpackage.tasks.forEach((t) => {
                        if (t.id === taskId) {
                            t.isMoving = false;
                            t.workPackageId = targetWorkPackageId; // Update the work package ID
                        }
                    });
                });
            });
        }
    }

    function handleDragStart(activeId: string) {
        const taskId = parseInt(activeId.substring(1));
        if (multiSelect) {
            selectMultiTask(taskId);
        } else {
            selectTask(taskId); // Ensure selectedTaskID is set before dragging
            const task = getTaskById(taskId);
            task.isMoving = true;
            setMovingTask(task);
            setMovingTaskOriginWP(task.workPackageId);
        }
    }

    function handleDragOver(targetGroup: string, targetId: string) {
        if (movingTask) {
            const targetWorkPackageId = parseInt(targetGroup.substring(2));
            const originWP = getWorkPackageById(movingTaskOriginWP!);

            if (("wp" + movingTask?.workPackageId) !== targetGroup) {

                // remove the task from the previous work package
                const taskId = movingTask.id;
                setWorkstreams((prevWorkstreams) => {
                    return prevWorkstreams.map((workstream) => {
                        return {
                            ...workstream,
                            workPackages: workstream.workPackages.map((workpackage) => {
                                if (workpackage.id === movingTask?.workPackageId) {
                                    return {
                                        ...workpackage,
                                        tasks: workpackage.tasks.filter((task) => task.id !== taskId),
                                    };
                                }
                                return workpackage;
                            })
                        };
                    });
                }
                );

                setMovingTask({
                    ...movingTask,
                    workPackageId: targetWorkPackageId
                });

                // add the task to the new work package
                setWorkstreams((prevWorkstreams) => {
                    return prevWorkstreams.map((workstream) => {
                        return {
                            ...workstream,
                            workPackages: workstream.workPackages.map((workpackage) => {
                                if (workpackage.id === targetWorkPackageId) {
                                    const newTaskList = (workpackage.index > originWP?.index!) ? [movingTask!, ...workpackage.tasks] : [...workpackage.tasks, movingTask!];
                                    return {
                                        ...workpackage,
                                        tasks: newTaskList,
                                    };
                                }
                                return workpackage;
                            })
                        };
                    });
                });
            } else {
                const currentWorkPackage = workstreams[0].workPackages.find((wp) => wp.id === targetWorkPackageId);
                const wpTasks = currentWorkPackage!.tasks;

                currentWorkPackage!.tasks =
                    arrayMove(
                        wpTasks,
                        wpTasks.findIndex((t) => t.id === movingTask.id),
                        wpTasks.findIndex((t) => t.id === parseInt(targetId.substring(1)))
                    );
            }
        }
    }

    function getContent() {
        try {
            if (app.authLevel > 3 && !lock.viewOnly && showEditForm) { // cant edit unless project manager or Admin
                if (multiSelect && selectedTaskIDs.length > 0) {
                    return <GroupEditTasksBuilder />
                }

                if (selectedTaskID !== null) {
                    const task = getTaskById(selectedTaskID);
                    if (task === undefined) {
                        return <div className="text-red-500">Task not found</div>;
                    }
                    return <TaskBuilder task={task} />
                }

                if (selectedWorkpackageID !== null) {
                    const workstream = projectTypeId === 2 ? workstreams.find((workstream) => workstream.id === selectedWorkstreamID) as Workstream : workstreams[0];
                    const workpackage = workstream.workPackages.find((workpackage) => workpackage.id === selectedWorkpackageID) as WorkPackage;
                    return <WorkPackageBuilder workPackage={workpackage} />
                }

                if (selectedWorkstreamID === null) {
                    return <ProjectBuilder projectName={app.selectedProject?.name ?? "Project"} projectTypeId={projectTypeId!} />
                }
                else {
                    const workstream = workstreams.find((workstream) => workstream.id === selectedWorkstreamID) as Workstream;
                    if (workstream === undefined) {
                        selectProject();
                    }
                    return <WorkstreamBuilder workstream={workstream} />
                }
            }
        } catch (error) {
            console.error("Error rendering content:", error);
            setSelectedTaskID(null);
            setSelectedWorkpackageID(null);
            setSelectedWorkstreamID(null);
            setNavLevel(0);
            app.showActionNotification("Error", "An error occurred while loading the content.", "destructive");
            setShowEditForm(false);
            return <div className="text-red-500">An error occurred while loading the content.</div>;
        }
    }

    function openAddMultipleTasksDialog() {
        setDialogContent(<FrmAddMultipleTasks setDialogOpen={setDialogOpen} />);
        setDialogTitle("Add Multiple Tasks");
        setDialogOpen(true);
    }

    function openTeamBuilderDialog(title: string = "You currently don't have a team for this project. Assign one here") {
        setDialogContent(<TeamBuilder />);
        setDialogTitle(title);
        setDialogOpen(true);
    }

    // function openGanttPDFExport() {
    //     setDialogContent(<PDFViewer style={{ width: "100%", height: "100%" }}>
    //         <ExportGanttPDF
    //             title={app.selectedProject?.name!}
    //             displayLength={displayLength}
    //             displayStartDate={displayStartDate}
    //             ganttScale={ganttScale}
    //             projectStartDate={projectStartDate}
    //             workstreams={workstreams}
    //             filteredTaskIDs={getVisibleTasks()} />
    //     </PDFViewer>);
    //     setDialogTitle("Export Gantt Chart to PDF");
    //     setDialogOpen(true);
    // }

    function openEditForm() {
        setShowEditForm(!showEditForm);
    }

    function ShowSavedMessage(name: string) {
        app.showActionNotification("Success", `${name} updated`, "success");
    }

    // this just runs when the page loads and whenever the selected project changes
    React.useEffect(() => {
        if (passedTaskId === undefined && passedWorkstreamId === undefined) {
            openTree(false);
            setSelectedTaskID(null);
            setSelectedWorkpackageID(null);
            setSelectedWorkstreamID(null);
            setNavLevel(0);
        }
    }, [app.selectedProject]);

    //#endregion

    //#region Keyboard Navigation

    const down = React.useCallback((e: KeyboardEvent) => {
        if (e.key === "ArrowDown" && e.ctrlKey) {
            e.preventDefault();
            if (navLevel === 3 && selectedTaskID !== null) {
                navigateTasks(1);
            }
            else if (navLevel === 2 && selectedWorkpackageID !== null) {
                navigateWorkPackages(1);
            }
            else if (navLevel === 1 && selectedWorkstreamID !== null) {
                navigateWorkStreams(1);
            }
        }
        else if (e.key === "ArrowUp" && e.ctrlKey) {
            e.preventDefault();
            if (navLevel === 3 && selectedTaskID !== null) {
                navigateTasks(-1);
            }
            else if (navLevel === 2 && selectedWorkpackageID !== null) {
                navigateWorkPackages(-1);
            }
            else if (navLevel === 1 && selectedWorkstreamID !== null) {
                navigateWorkStreams(-1);
            }
        }
        else if (e.key === "PageUp") {
            e.preventDefault();
            if (navLevel === 3) {
                navigateWorkPackages(0);
                setNavLevel(2);
            }
            else if (navLevel === 2) {
                if (projectTypeId === 2) {
                    selectWorkstream(selectedWorkstreamID as number);
                } else {
                    selectProject();
                }
            }
            else if (navLevel === 1) {
                selectWorkstream(workstreams[workstreams.length - 1].id);
            }
            else if (navLevel === 0) {
                return;
            }
        }
        else if (e.key === "PageDown") {
            e.preventDefault();
            if (navLevel === 3) {
                return;
            }
            else if (navLevel === 2) {
                navigateTasks(0);
                setNavLevel(3);
            }
            else if (navLevel === 1) {
                navigateWorkPackages(0);
                setNavLevel(2);
            }
            else if (navLevel === 0) {
                if (projectTypeId === 2) {
                    selectWorkstream(workstreams[0].id);
                } else {
                    selectWorkPackage(workstreams[0].workPackages[0].id);
                }
            }
        }
        else if ((e.key === "+" || e.key === "=") && e.ctrlKey && app.authLevel > 3 && !lock.viewOnly) {
            e.preventDefault();

            if (app.teamMembers.length == 0 && app.projectManagers.length == 0) {
                openTeamBuilderDialog();
                return;
            }

            if (navLevel === 0) {
                // addWorkstream();
            }
            else if (navLevel === 1) {
                addWorkPackage();
            }
            else if (navLevel === 2) {
                addTask();
            }
        }
        else if ((e.key === "m") && e.ctrlKey && app.authLevel > 3 && !lock.viewOnly) {
            e.preventDefault();

            if (navLevel === 2 || navLevel === 3) {
                openAddMultipleTasksDialog();
            }
        }
        else if ((e.key === "m") && e.altKey) {
            e.preventDefault();
            toggleMultiSelect();
        }
        else if ((e.key === "g") && e.altKey) {
            e.preventDefault();
            setShowGantt(prev => !prev);
            if (!showGantt) {
                setShowChart(false);
                openTree(true);
            }
        }
        else if ((e.key === "p") && e.altKey) {
            e.preventDefault();
            setShowChart(prev => !prev);
            if (!showChart) {
                setShowGantt(false);
                openTree(false);
            }
        }
        else if ((e.key === "e") && e.altKey && app.authLevel > 3 && !lock.viewOnly) {
            e.preventDefault();
            setShowEditForm(prev => !prev);
        }
        else if ((e.key === "t") && e.altKey && app.authLevel > 3 && !lock.viewOnly) {
            e.preventDefault();
            openTeamBuilderDialog("Manage Team");
        }
        else if (e.key === "i" && e.altKey && app.authLevel > 3 && !lock.viewOnly) {
            e.preventDefault();
            indentTasksUnderParent();
        }
        else if (e.key === "o" && e.altKey && app.authLevel > 3 && !lock.viewOnly) {
            e.preventDefault();
            outdentTasksFromParent();
        }
        else if (e.key === "d" && e.altKey && app.authLevel > 3 && !lock.viewOnly) {
            e.preventDefault();
            if (navLevel === 3 && selectedTaskID !== null) {
                const tasks = multiSelect ? selectedTaskIDs : [selectedTaskID];
                confirmDeleteTasks(tasks);
            }
            else if (navLevel === 2 && selectedWorkpackageID !== null) {
                confirmDeleteWorkPackage(selectedWorkpackageID);
            }
            // else if (navLevel === 1 && selectedWorkstreamID !== null) {
            //     deleteWorkstream(selectedWorkstreamID);
            // }
        }
    }, [navLevel, selectedTaskID, selectedWorkpackageID, selectedWorkstreamID, workstreams, selectedTaskIDs, multiSelect]);

    React.useEffect(() => {
        document.addEventListener("keydown", down);
        return () => document.removeEventListener("keydown", down);
    }, [down]);

    // scroll to selected item
    React.useEffect(() => {
        if (selectedTaskID !== null) {
            document.getElementById("t" + selectedTaskID)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        else if (selectedWorkpackageID !== null) {
            document.getElementById("wp" + selectedWorkpackageID)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        else if (selectedWorkstreamID !== null) {
            document.getElementById("ws" + selectedWorkstreamID)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [selectedWorkstreamID, selectedWorkpackageID, selectedTaskID]);

    //#endregion

    const contextValue = {
        projectStartDate,
        updateProjectStartDate,
        projectLifecycleStage,
        workstreams,
        selectedWorkstreamID,
        selectedWorkpackageID,
        selectedTaskID,
        selectedTaskIDs,
        openWorkstreams,
        openWorkpackages,
        multiSelect,
        setOpenWorkstreams,
        selectWorkstream,
        selectWorkPackage,
        selectTask,
        selectMultiTask,
        setWorkstreams,
        addWorkstream,
        updateWorkstream,
        updateWorkPackage,
        deleteWorkstream,
        addWorkPackage,
        deleteWorkPackage,
        confirmDeleteWorkPackage,
        addTask,
        getTaskById,
        getWorkPackageById,
        getAllTasks,
        getDependentTaskIds,
        updateTask,
        groupEditTasks,
        toggleWorkstream,
        toggleWorkPackage,
        setShowEditForm,
        deleteTask,
        groupDeleteTasks,
        confirmDeleteTasks,
        addMultipleTasks,
        completeMultipleTasks,
        getDependents: getDependentNames,
        isWorkingDay,
        addWorkingDays,
        getWorkingDays,
        updateDependentDates,
        saveTaskOrder,
        indentTasksUnderParent,
        outdentTasksFromParent,
    };

    return (
        <PlanBuilderContext.Provider value={contextValue}>
            <GanttContext.Provider value={{ displayLength, displayStartDate, setDisplayStartDate, setDisplayLength, ganttScale, setGanttScale, projectStartDate, projectEndDate, scaleToSelected }}>
                <div className="flex flex-col overflow-hidden">
                    <div className="flex flex-row justify-center justify-between items-center">
                        <div className="flex flex-row text-2xl font-bold">Plan{lock.getRefreshControl()}</div>
                        {/* <button
                            className='flex flex-row w-min h-8 ml-auto bg-black text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-gray-700'
                            onClick={() => openGanttPDFExport()}
                        >
                            <DownloadIcon tooltip="Export Gantt to PDF" className="h-4 w-4 mr-2" /><span className="flex items-center h-full">Export</span>
                        </button> */}
                        <div className="flex flex-row space-x-2">
                            {app.authLevel > 3 && lock.getLockControl()}
                            <HelpLink page="plan" />
                        </div>
                    </div>
                    <div className="flex flex-row border-2 border-gray-500 w-full rounded-md mr-2 mb-2  justify-between p-2">
                        <Input type="text" placeholder="Search Tasks..." className="w-[280px] p-2" onKeyUp={(value) => setSearchText(value.currentTarget.value)} />
                        <div className="flex flex-row space-x-2">
                            <button onClick={() => setFilterByDeliverable(!filterByDeliverable)} className={"p-2 rounded-md " + (filterByDeliverable ? "bg-table-subheader" : "")}><DeliverableIcon /></button>
                            <button onClick={() => setFilterByMilestone(!filterByMilestone)} className={"p-2 rounded-md " + (filterByMilestone ? "bg-table-subheader" : ""/*"bg-planbuilder-milestone hover: none" : "bg-planbuilder-milestone-unselected"*/)} ><MilestoneIcon /></button>
                            <button onClick={() => setFilterByCompleted(!filterByCompleted)} className={"p-2 rounded-md " + (filterByCompleted ? "bg-table-subheader" : "")} ><CheckIcon /></button>
                            <button onClick={() => setFilterByCharter(!filterByCharter)} className={"p-2 rounded-md " + (filterByCharter ? "bg-table-subheader" : "")} ><CharterIcon /></button>
                            <button onClick={() => setFilterByStatusReport(!filterByStatusReport)} className={"p-2 rounded-md " + (filterByStatusReport ? "bg-table-subheader" : "")} ><StatusReportIcon /></button>
                            <button onClick={() => { setFilterByOwner(!filterByOwner); setFilterByUnassigned(false); }} className={"p-2 rounded-md " + (filterByOwner ? "bg-table-subheader" : "")} ><UserIcon /></button>
                            <button onClick={() => { setFilterByUnassigned(!filterByUnassigned); setFilterByOwner(false); }} className={"p-2 rounded-md " + (filterByUnassigned ? "bg-table-subheader" : "")} ><UnassignedIcon /></button>
                            <button onClick={() => { setFilterByStatusAmber(!filterByStatusAmber); setFilterByStatusRed(false); }} className={"p-2 rounded-md " + (filterByStatusAmber ? "bg-table-subheader" : "")} ><TrafficLightComponent TrafficLightColour={2} Size={6} Animate={false} /></button>
                            <button onClick={() => { setFilterByStatusRed(!filterByStatusRed); setFilterByStatusAmber(false); }} className={"p-2 rounded-md " + (filterByStatusRed ? "bg-table-subheader" : "")} ><TrafficLightComponent TrafficLightColour={1} Size={6} Animate={false} /></button>
                            <button onClick={() => setFilterByDependencies(!filterByDependencies)} className={"p-2 rounded-md " + (filterByDependencies ? "bg-table-subheader" : "")} disabled={selectedTaskID == null} ><DependenciesIcon className={(selectedTaskID == null ? "text-gray-500" : "")} /></button>
                            <button onClick={() => { setFilterBySelected(!filterBySelected); }} className={"p-2 rounded-md " + (filterBySelected ? "bg-table-subheader" : "")} disabled={!enableFilterBySelected}><SelectedIcon className={(!enableFilterBySelected ? "text-gray-500" : "")} /></button>
                        </div>
                        <div className="flex flex-row space-x-2">
                            <button onClick={() => toggleChart()} className={"p-2 rounded-md " + (showChart ? "bg-table-subheader" : "")} ><ChartIcon /></button>
                            <button onClick={() => toggleGantt()} className={"p-2 rounded-md " + (showGantt ? "bg-table-subheader" : "")} ><GanttChartIcon /></button>
                            {app.authLevel > 3 && !lock.viewOnly &&
                                (<>
                                    <button onClick={() => openTeamBuilderDialog("Manage Your Team")} className={"p-2 rounded-md"}><ManageTeamIcon /></button>
                                    <button onClick={() => openAddMultipleTasksDialog()} className={"p-2 rounded-md"} disabled={disableAddMultiTasks}><MultiAddIcon className={(disableAddMultiTasks ? "text-gray-500" : "")} /></button>
                                </>)}
                            <button onClick={(event) => toggleMultiSelect(event)} className={"p-2 rounded-md " + (multiSelect ? "bg-table-subheader" : "")} ><MultiSelectIcon /></button>
                            {app.authLevel > 3 && !lock.viewOnly && (
                                <button onClick={() => { openEditForm(); }} className={"p-2 rounded-md " + (showEditForm ? "bg-table-subheader" : "")} ><EditIcon /></button>
                            )}
                            <button onClick={() => openTree(!openStateAll)} className={"p-2 rounded-md"} >{openStateAll ? <ArrowUpIcon /> : <ArrowDownIcon />}</button>
                        </div>
                    </div>
                    {showGantt && <GanttControls />}
                    <div className="flex flex-row">
                        <div className={"flex flex-col w-full " + (showGantt ? "h-[670px]" : "h-[750px]")}>
                            <div className="flex flex-col border-2 border-gray-500 w-full rounded-md overflow-y-auto scrollbar">
                                <ProjectObj
                                    name={app.selectedProject?.name ?? "Project"}
                                    onClick={() => selectProject()}
                                    workstreams={workstreams}
                                    showChart={showChart}
                                    showGantt={showGantt}
                                    projectStartDate={projectStartDate}
                                    projectEndDate={projectEndDate}
                                />
                                <DragDropProvider
                                    onDragEnd={({ operation }) => {
                                        const { source, target } = operation;
                                        if (isSortable(source) && target) {
                                            if (isSortable(target) && target.sortable.group) {
                                                const sourceGroup = source.sortable.group?.toString();
                                                const targetGroup = target.sortable.group.toString();
                                                if (target.type === "task" && operation.source?.type === "task"
                                                    && sourceGroup?.substring(0, 2) == "wp" && targetGroup.substring(0, 2) == "wp") {
                                                    handleDragEnd(targetGroup, source.id.toString(), target.id.toString());
                                                }
                                                else if (target.type === "workPackage" && operation.source?.type === "workPackage") {
                                                    saveWorkPackageOrder(parseInt(target.sortable.group.toString().substring(2)));
                                                }
                                            }
                                        }
                                    }}

                                    onDragStart={(event) => {
                                        const { source } = event.operation;
                                        if (isSortable(source)) {
                                            if (source.type === "task") {
                                                handleDragStart(source.id.toString());
                                            }
                                        }
                                    }}

                                    onDragOver={
                                        ({ operation }) => {
                                            const { source, target } = operation;
                                            if (isSortable(source) && isSortable(target) && target.sortable.group) {
                                                if (target.sortable.group.toString().substring(0, 2) === "wp" && target.type === "task" && operation.source?.type === "task") {
                                                    handleDragOver(target.sortable.group.toString(), target.id.toString());
                                                }
                                                else if (target.type === "workPackage" && operation.source?.type === "workPackage") {
                                                    const wps = workstreams[0].workPackages;
                                                    workstreams[0].workPackages =
                                                        arrayMove(
                                                            wps,
                                                            wps.findIndex((wp) => wp.id === parseInt(operation.source?.id.toString().substring(2)!)),
                                                            wps.findIndex((wp) => wp.id === parseInt(target.id.toString().substring(2)))
                                                        );
                                                }
                                            }
                                        }}

                                    sensors={[
                                        PointerSensor.configure({
                                            activationConstraints: {
                                                distance: {
                                                    value: 25,
                                                    tolerance: { x: 200, y: 25 },
                                                },

                                                delay: {
                                                    value: 1000,
                                                    tolerance: 10,
                                                }
                                            },
                                        }),
                                        KeyboardSensor,
                                    ]}
                                >
                                    {workstreams.map((workstream) => (
                                        <WorkstreamObj workstream={workstream}
                                            key={"ws" + workstream.id}
                                            onClick={() => selectWorkstream(workstream.id)}
                                            selected={selectedWorkstreamID === workstream.id}
                                            open={openWorkstreams?.includes(workstream.id)}
                                            projectTypeId={projectTypeId ?? 1}
                                            showChart={showChart}
                                            showGantt={showGantt}
                                        />
                                    ))}
                                    <DragOverlay>
                                        {movingTask ? (
                                            <TaskObj
                                                task={{ ...movingTask, isMoving: false }}
                                                selected={multiSelect ? selectedTaskIDs.includes(movingTask.id) : selectedTaskID === movingTask.id}
                                                primary={multiSelect ? selectedTaskID === movingTask.id : false}
                                                showGantt={showGantt}
                                                index={movingTask.index}
                                                subTaskDepth={0}
                                                groupId={"wp" + movingTask.workPackageId}
                                                expanded={false}
                                            />
                                        ) : null}
                                    </DragOverlay>
                                </DragDropProvider>
                            </div>
                        </div>
                        {app.authLevel > 3 && !lock.viewOnly && (
                            getContent() /* display relevant form */
                        )}
                    </ div>

                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogContent
                            className="flex flex-col min-w-fit"
                            style={
                                dialogTitle === "Export Gantt Chart to PDF"
                                    ? { minWidth: "80vw", minHeight: "80vh", width: "80vw", height: "80vh", maxWidth: "90vw", maxHeight: "90vh" }
                                    : undefined
                            }
                        >
                            <DialogHeader>
                                <DialogTitle>{dialogTitle}</DialogTitle>
                            </DialogHeader>
                            {dialogContent}
                        </DialogContent>
                    </Dialog>
                </ div>
            </GanttContext.Provider>
        </PlanBuilderContext.Provider>
    )
}

export default PlanBuilder;

